import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { auth, db, storage, functions } from '../../firebase.config';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, getDoc, setDoc, deleteField, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, getMetadata, deleteObject } from 'firebase/storage';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { FaUsers, FaEnvelope, FaBriefcase, FaSignOutAlt, FaPlus, FaEdit, FaTrash, FaFilePdf, FaFileUpload, FaNewspaper, FaUserCheck, FaImage, FaFileAlt, FaDownload, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import Loader from '../../components/Loader/Loader';
import BlogContentEditor from '../../components/BlogContentEditor/BlogContentEditor';
import { sanitizeBlogHtml, isRichTextEmpty } from '../../utils/sanitizeBlogHtml';
import { ensureUniqueInsightSlug, repairDuplicateInsightSlugsInFirestore } from '../../utils/insightArticle';
import { downloadCsv } from '../../utils/csvDownload';
import { userMessageFromCallableError } from '../../lib/firebaseCallableErrors';
import { FUND_DECK_PHONE_ERROR, isValidFundDeckPhone } from '../../lib/fundDeckPhone';
import './AdminDashboard.css';

function formDataTrimmedString(formData: FormData, key: string): string {
  const v = formData.get(key);
  return typeof v === 'string' ? v.trim() : '';
}

interface TeamMember {
  id: string;
  name: string;
  role: string;
  bio: string;
  photoURL?: string;
  order: number;
  creds?: string[];
  email?: string;
  linkedin?: string;
  previouslyAt?: string[];
  education?: string[];
  /** When false, hidden on public Home / About team sections. Omitted defaults to visible. */
  showOnWebsite?: boolean;
}

interface ContactInquiry {
  id: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  organisation?: string;
  timestamp: any;
  status: string;
}

interface CareerApplication {
  id: string;
  name: string;
  email: string;
  phone: string;
  role?: string;
  resumeURL?: string;
  timestamp: any;
  status: string;
}

interface DeckRequest {
  id: string;
  name: string;
  phone: string;
  organisation?: string | null;
  email?: string | null;
  timestamp: any;
  status: string;
  /** Set when the lead first loads the on-screen fund deck (server). */
  deckOpenedAt?: any;
}

interface GovernanceRequest {
  id: string;
  documentKey: string;
  documentLabel: string;
  name: string;
  phone: string;
  organisation?: string | null;
  email?: string | null;
  timestamp: any;
  status: string;
}

interface Report {
  id: string;
  title: string;
  type: 'monthly' | 'quarterly' | 'annual';
  date: any;
  fileURL: string;
  published: boolean;
}

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  date: any;
  published: boolean;
  imageURL?: string;
  /** Stable public URL segment under /insights/:slug */
  slug?: string;
}

interface Deal {
  id: string;
  type: string;
  typeClass: 'preipo' | 'ipo' | 'debt';
  company: string;
  sector: string;
  amount: string;
  amountLabel: string;
  desc: string;
  date: string;
  order: number;
}

interface DealGallery {
  id: string;
  slug: string;
  title: string;
  caption: string;
  imageURLs: string[];
  order: number;
}

const GOVERNANCE_DOC_LABELS: Record<string, string> = {
  ppm: 'Private Placement Memorandum (PPM)',
  'contribution-agreement': 'Contribution Agreement',
  'sebi-compliance': 'SEBI Compliance Filings',
};

/** Canonical fund-deck PDF on the default Storage bucket; legacy path still detected for older uploads. */
const FUND_DECK_PDF_STORAGE_PATH = 'fund-deck/fund-deck.pdf';
const FUND_DECK_PDF_LEGACY_STORAGE_PATH = 'sample-deck/sample-deck.pdf';

async function fundDeckPdfExistsInDefaultBucket(): Promise<boolean> {
  for (const path of [FUND_DECK_PDF_STORAGE_PATH, FUND_DECK_PDF_LEGACY_STORAGE_PATH]) {
    try {
      await getMetadata(ref(storage, path));
      return true;
    } catch {
      /* try next */
    }
  }
  return false;
}

/** Single OK/Cancel confirm for destructive admin actions (no typed confirmation). */
function adminDeleteConfirm(message: string): boolean {
  return window.confirm(message);
}

type AdminRequestListId = 'contacts' | 'careers' | 'deck' | 'governance';

type AdminRequestListPaging = {
  dateFrom: string;
  dateTo: string;
  page: number;
  pageSize: number;
};

const ADMIN_REQUEST_LIST_PAGING_INITIAL: AdminRequestListPaging = {
  dateFrom: '',
  dateTo: '',
  page: 1,
  pageSize: 25,
};

type FirestoreTimestampField = { toDate?: () => Date } | null | undefined;

function timestampFieldToMs(t: FirestoreTimestampField): number | null {
  const fn = t?.toDate;
  if (typeof fn !== 'function') return null;
  try {
    const d = fn.call(t);
    if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
    return d.getTime();
  } catch {
    return null;
  }
}

/** Local calendar bounds for `<input type="date" value="YYYY-MM-DD">`. */
function dateInputLocalStartMs(iso: string): number | null {
  const s = iso.trim();
  if (!s) return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

function dateInputLocalEndMs(iso: string): number | null {
  const s = iso.trim();
  if (!s) return null;
  const d = new Date(`${s}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

function matchesSubmittedDateRange(ms: number | null, dateFrom: string, dateTo: string): boolean {
  if (!dateFrom.trim() && !dateTo.trim()) return true;
  if (ms == null) return false;
  const from = dateInputLocalStartMs(dateFrom);
  const to = dateInputLocalEndMs(dateTo);
  if (from != null && ms < from) return false;
  if (to != null && ms > to) return false;
  return true;
}

function AdminRequestDateFilters(props: {
  dateFrom: string;
  dateTo: string;
  pageSize: number;
  onDateFrom: (v: string) => void;
  onDateTo: (v: string) => void;
  onPageSize: (n: number) => void;
}) {
  const { dateFrom, dateTo, pageSize, onDateFrom, onDateTo, onPageSize } = props;
  return (
    <>
      <label className="admin-date-filter-label">
        <span>From</span>
        <input type="date" className="admin-date-input" value={dateFrom} onChange={(e) => onDateFrom(e.target.value)} />
      </label>
      <label className="admin-date-filter-label">
        <span>To</span>
        <input type="date" className="admin-date-input" value={dateTo} onChange={(e) => onDateTo(e.target.value)} />
      </label>
      <label className="admin-date-filter-label">
        <span>Per page</span>
        <select
          className="status-select"
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          aria-label="Rows per page"
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </label>
    </>
  );
}

function AdminRequestPaginationRow(props: {
  page: number;
  totalPages: number;
  totalFiltered: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  const { page, totalPages, totalFiltered, onPrev, onNext } = props;
  if (totalFiltered === 0) return null;
  return (
    <div className="admin-request-pagination" role="navigation" aria-label="Paged results">
      <span>
        Page {page} of {totalPages}
      </span>
      <span className="admin-request-pagination-meta">{totalFiltered} matching</span>
      <button type="button" className="cancel-btn" disabled={page <= 1} onClick={onPrev}>
        Previous
      </button>
      <button type="button" className="cancel-btn" disabled={page >= totalPages} onClick={onNext}>
        Next
      </button>
    </div>
  );
}

const DEFAULT_TEAM: Array<Partial<TeamMember> & { name: string; role: string; bio: string; photoURL: string }> = [
  { name: 'Aman Maheshwari', role: 'Founder & Fund Manager · IC Member', bio: '10+ years across Asian capital markets. Former Goldman Sachs, JPMorgan Chase & Nomura Holdings. Leads investment strategy with focus on disciplined portfolio construction, capital preservation and generating consistent risk-adjusted returns.', photoURL: '/assets/team/aman-maheshwari.jpeg', creds: ['MBA Finance, IBS Hyderabad', 'NISM XIX-C'], education: ['MBA Finance - IBS Hyderabad', 'NISM XIX-C: AIF Certified'], previouslyAt: ['Goldman Sachs', 'JPMorgan Chase', 'NOMURA'] },
  { name: 'CA Rachna Chelawat', role: 'Head of Finance & Due Diligence · IC Member', bio: 'Chartered Accountant with expertise in financial evaluation, regulatory compliance, taxation and corporate structuring. Leads financial modelling, fund oversight and investment decision support at Kyro Asset Management.', photoURL: '/assets/team/rachna-chelawat.jpeg', creds: ['Chartered Accountant (CA)'], education: ['Chartered Accountant (CA)'], previouslyAt: [] },
  { name: 'Tanushree Patidar', role: 'Compliance Officer', bio: 'Strong foundation in corporate governance, regulatory frameworks and structured transaction advisory. Actively contributes to due diligence, financial analysis and IPO-related documentation ensuring adherence to all regulatory standards.', photoURL: '/assets/team/tanushree-patidar.jpeg', creds: ['Regulatory Frameworks', 'IPO Documentation'], education: ['Regulatory Frameworks', 'IPO Documentation'], previouslyAt: [] },
  { name: 'Avish Jain', role: 'Investment Banking Associate', bio: 'Specialises in valuation, financial modelling and transaction analysis. Executes capital market mandates by developing detailed financial models, investor materials and supporting advisory engagements including comprehensive IPO documentation.', photoURL: '/assets/team/avish-jain.jpeg', creds: ['Valuation', 'Financial Modelling', 'Transaction Execution'], education: ['Valuation', 'Financial Modelling', 'Transaction Execution'], previouslyAt: [] },
];

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'team' | 'contacts' | 'careers' | 'deck' | 'governance' | 'reports' | 'blog' | 'investors' | 'content' | 'deals'>('team');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [contacts, setContacts] = useState<ContactInquiry[]>([]);
  const [careers, setCareers] = useState<CareerApplication[]>([]);
  const [deckRequests, setDeckRequests] = useState<DeckRequest[]>([]);
  const [deckDeletingId, setDeckDeletingId] = useState<string | null>(null);
  const [teamDeletingId, setTeamDeletingId] = useState<string | null>(null);
  const [contactDeletingId, setContactDeletingId] = useState<string | null>(null);
  const [careerDeletingId, setCareerDeletingId] = useState<string | null>(null);
  const [governanceRequests, setGovernanceRequests] = useState<GovernanceRequest[]>([]);
  const [reports, setReports] = useState<Report[]>([]);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [showReportForm, setShowReportForm] = useState(false);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState<BlogPost | null>(null);
  const blogImageInputRef = useRef<HTMLInputElement>(null);
  const [blogImageUploading, setBlogImageUploading] = useState(false);
  const [blogContentHtml, setBlogContentHtml] = useState('');
  const [investorEmails, setInvestorEmails] = useState<string[]>([]);
  const [csvUploading, setCsvUploading] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [addingEmail, setAddingEmail] = useState(false);
  const [editingInvestorEmail, setEditingInvestorEmail] = useState<string | null>(null);
  const [editingInvestorValue, setEditingInvestorValue] = useState('');
  const [editingReport, setEditingReport] = useState<Report | null>(null);
  const [editingContact, setEditingContact] = useState<ContactInquiry | null>(null);
  const [editingDeck, setEditingDeck] = useState<DeckRequest | null>(null);
  const [editingGov, setEditingGov] = useState<GovernanceRequest | null>(null);
  const [deckFilterStatus, setDeckFilterStatus] = useState<string>('all');
  const [deckSearch, setDeckSearch] = useState('');
  const [govFilterStatus, setGovFilterStatus] = useState<string>('all');
  const [govFilterDoc, setGovFilterDoc] = useState<string>('all');
  const [govSearch, setGovSearch] = useState('');
  const [contactFilterStatus, setContactFilterStatus] = useState<string>('all');
  const [contactSearch, setContactSearch] = useState('');
  const [careerFilterStatus, setCareerFilterStatus] = useState<string>('all');
  const [careerSearch, setCareerSearch] = useState('');
  const [requestListPaging, setRequestListPaging] = useState<Record<AdminRequestListId, AdminRequestListPaging>>(() => ({
    contacts: { ...ADMIN_REQUEST_LIST_PAGING_INITIAL },
    careers: { ...ADMIN_REQUEST_LIST_PAGING_INITIAL },
    deck: { ...ADMIN_REQUEST_LIST_PAGING_INITIAL },
    governance: { ...ADMIN_REQUEST_LIST_PAGING_INITIAL },
  }));
  const [editingCareer, setEditingCareer] = useState<CareerApplication | null>(null);
  const [sampleDeckUrl, setSampleDeckUrl] = useState<string | null>(null);
  const [sampleDeckUploading, setSampleDeckUploading] = useState(false);
  const [sampleDeckProcessingStatus, setSampleDeckProcessingStatus] = useState<string | null>(null);
  const [sampleDeckPageCount, setSampleDeckPageCount] = useState(0);
  const [sampleDeckProcessingError, setSampleDeckProcessingError] = useState<string | null>(null);
  /** Whether the fund-deck PDF exists on the default Storage bucket (canonical or legacy path). */
  const [sampleDeckPdfExists, setSampleDeckPdfExists] = useState<boolean | null>(null);
  const [deckPreviewUrls, setDeckPreviewUrls] = useState<string[] | null>(null);
  const [deckPreviewLoading, setDeckPreviewLoading] = useState(false);
  const [deckPreviewError, setDeckPreviewError] = useState<string | null>(null);
  const [deckPreviewRefreshKey, setDeckPreviewRefreshKey] = useState(0);
  const [rebuildingDeckPages, setRebuildingDeckPages] = useState(false);
  const [homeStats, setHomeStats] = useState({ targetIRR: '35%', minInvestment: '₹1 Cr', transactionsAdvised: '₹550 Cr+', yearsAsianMarkets: '10+' });
  const [deals, setDeals] = useState<Deal[]>([]);
  const [dealGalleries, setDealGalleries] = useState<DealGallery[]>([]);
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null);
  const [showDealForm, setShowDealForm] = useState(false);
  const [editingGallery, setEditingGallery] = useState<DealGallery | null>(null);
  const [galleryImageUploading, setGalleryImageUploading] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [seedingTeam, setSeedingTeam] = useState(false);
  const navigate = useNavigate();

  const patchRequestListPaging = useCallback((id: AdminRequestListId, patch: Partial<AdminRequestListPaging>) => {
    setRequestListPaging((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate('/admin-login');
        return;
      }
      const phone = user.phoneNumber || (user.providerData?.[0]?.phoneNumber);
      if (!phone) {
        navigate('/admin-login');
        return;
      }
      const digits = phone.replace(/\D/g, '');
      const altIds = digits.length === 10
        ? [digits]
        : [digits.startsWith('91') ? digits : `91${digits}`, digits.slice(-10)];
      let adminDoc = await getDoc(doc(db, 'adminUsers', phone));
      for (const id of altIds) {
        if (!adminDoc.exists()) adminDoc = await getDoc(doc(db, 'adminUsers', id));
      }
      if (!adminDoc.exists()) {
        await signOut(auth);
        navigate('/admin-login');
        return;
      }
      loadData();
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!showBlogForm) return;
    setBlogContentHtml(editingBlog?.content ?? '');
  }, [editingBlog, showBlogForm]);

  const loadData = async () => {
    setLoading(true);
    try {
      const teamQuery = query(collection(db, 'teamMembers'), orderBy('order'));
      const teamSnapshot = await getDocs(teamQuery);
      setTeamMembers(teamSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as TeamMember)));

      const contactsQuery = query(collection(db, 'contactInquiries'), orderBy('timestamp', 'desc'));
      const contactsSnapshot = await getDocs(contactsQuery);
      setContacts(contactsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ContactInquiry)));

      const careersQuery = query(collection(db, 'careerApplications'), orderBy('timestamp', 'desc'));
      const careersSnapshot = await getDocs(careersQuery);
      setCareers(careersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CareerApplication)));

      const deckQuery = query(collection(db, 'deckRequests'), orderBy('timestamp', 'desc'));
      const deckSnapshot = await getDocs(deckQuery);
      setDeckRequests(deckSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DeckRequest)));

      const govQuery = query(collection(db, 'governanceRequests'), orderBy('timestamp', 'desc'));
      const govSnapshot = await getDocs(govQuery);
      setGovernanceRequests(govSnapshot.docs.map((d) => ({ id: d.id, ...d.data() } as GovernanceRequest)));

      const reportsQuery = query(collection(db, 'reports'), orderBy('date', 'desc'));
      const reportsSnapshot = await getDocs(reportsQuery);
      setReports(reportsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Report)));

      const blogQuery = query(collection(db, 'blogPosts'), orderBy('date', 'desc'));
      const blogSnapshot = await getDocs(blogQuery);
      let blogRows = blogSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() } as BlogPost));
      for (const d of blogSnapshot.docs) {
        const data = d.data();
        const title = typeof data.title === 'string' ? data.title.trim() : '';
        const existing = typeof data.slug === 'string' ? data.slug.trim() : '';
        if (existing || !title) continue;
        try {
          const slug = await ensureUniqueInsightSlug(title, d.id);
          await updateDoc(doc(db, 'blogPosts', d.id), { slug });
          blogRows = blogRows.map((p) => (p.id === d.id ? { ...p, slug } : p));
        } catch (e) {
          console.error('Blog slug backfill failed for', d.id, e);
        }
      }
      try {
        blogRows = await repairDuplicateInsightSlugsInFirestore(blogRows);
      } catch (e) {
        console.error('Blog duplicate slug repair failed', e);
      }
      setBlogPosts(blogRows);

      const whitelistDoc = await getDoc(doc(db, 'investorEmails', 'whitelist'));
      setInvestorEmails((whitelistDoc.data()?.emails as string[]) || []);

      const siteConfigDoc = await getDoc(doc(db, 'siteConfig', 'sampleDeck'));
      const sd = siteConfigDoc.data();
      setSampleDeckUrl(sd?.fileURL || null);
      const ps = (sd?.processingStatus as string) || null;
      setSampleDeckProcessingStatus(ps === 'pending' ? 'processing' : ps);
      setSampleDeckPageCount(typeof sd?.pageCount === 'number' ? sd.pageCount : 0);
      setSampleDeckProcessingError((sd?.processingError as string) || null);
      setSampleDeckPdfExists(await fundDeckPdfExistsInDefaultBucket());

      const homeStatsDoc = await getDoc(doc(db, 'siteConfig', 'homeStats'));
      const hs = homeStatsDoc.data();
      if (hs) setHomeStats({ targetIRR: hs.targetIRR || '35%', minInvestment: hs.minInvestment || '₹1 Cr', transactionsAdvised: hs.transactionsAdvised || '₹550 Cr+', yearsAsianMarkets: hs.yearsAsianMarkets || '10+' });

      const dealsQuery = query(collection(db, 'deals'), orderBy('order'));
      const dealsSnapshot = await getDocs(dealsQuery);
      setDeals(dealsSnapshot.docs.map(d => ({ id: d.id, ...d.data() } as Deal)));

      const galleriesQuery = query(collection(db, 'dealGalleries'), orderBy('order'));
      const galleriesSnapshot = await getDocs(galleriesQuery);
      const galleryList = galleriesSnapshot.docs
        .map(
          (d) =>
            ({
              id: d.id,
              ...d.data(),
              imageURLs: d.data().imageURLs || [],
              order: typeof d.data().order === 'number' ? d.data().order : 0,
            } as DealGallery)
        )
        .sort((a, b) => a.order - b.order);
      setDealGalleries(galleryList);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshSampleDeckPanel = useCallback(async () => {
    try {
      setSampleDeckPdfExists(await fundDeckPdfExistsInDefaultBucket());
      const siteConfigDoc = await getDoc(doc(db, 'siteConfig', 'sampleDeck'));
      const sd = siteConfigDoc.data();
      setSampleDeckUrl(sd?.fileURL || null);
      const ps = (sd?.processingStatus as string) || null;
      setSampleDeckProcessingStatus(ps === 'pending' ? 'processing' : ps);
      setSampleDeckPageCount(typeof sd?.pageCount === 'number' ? sd.pageCount : 0);
      setSampleDeckProcessingError((sd?.processingError as string) || null);
    } catch (error) {
      console.error('refreshSampleDeckPanel:', error);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'deck') return;
    void refreshSampleDeckPanel();
  }, [activeTab, refreshSampleDeckPanel]);

  useEffect(() => {
    if (activeTab !== 'deck') return;
    if (sampleDeckProcessingStatus !== 'ready' || sampleDeckPageCount < 1) {
      setDeckPreviewUrls(null);
      setDeckPreviewError(null);
      setDeckPreviewLoading(false);
      return;
    }
    let cancelled = false;
    setDeckPreviewLoading(true);
    setDeckPreviewError(null);
    (async () => {
      try {
        const fn = httpsCallable(functions, 'getAdminSampleDeckPreview', { timeout: 90_000 });
        const res = await fn();
        const d = res.data as {
          ok?: boolean;
          code?: string;
          pageUrls?: string[];
          message?: string;
          processingStatus?: string;
        };
        if (cancelled) return;
        if (d.ok && Array.isArray(d.pageUrls) && d.pageUrls.length > 0) {
          setDeckPreviewUrls(d.pageUrls);
          setDeckPreviewError(null);
        } else {
          setDeckPreviewUrls(null);
          if (d.code === 'NOT_READY') {
            setDeckPreviewError(`Deck not ready (${d.processingStatus || 'unknown'}).`);
          } else {
            setDeckPreviewError(d.message || 'Could not load slide preview.');
          }
        }
      } catch (e) {
        if (!cancelled) {
          setDeckPreviewUrls(null);
          setDeckPreviewError(userMessageFromCallableError(e));
        }
      } finally {
        if (!cancelled) setDeckPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, sampleDeckProcessingStatus, sampleDeckPageCount, deckPreviewRefreshKey]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/admin-login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleAddTeamMember = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      let photoURL = '';
      const photoFile = formData.get('photo') as File;

      if (photoFile && photoFile.size > 0) {
        const photoRef = ref(storage, `team/${Date.now()}_${photoFile.name}`);
        await uploadBytes(photoRef, photoFile);
        photoURL = await getDownloadURL(photoRef);
      }

      const credsStr = (formData.get('creds') as string)?.trim();
      const creds = credsStr ? credsStr.split(/[\n,]+/).map(s => s.trim()).filter(Boolean) : [];
      const educationStr = (formData.get('education') as string)?.trim();
      const education = educationStr ? educationStr.split(/[\n,]+/).map(s => s.trim()).filter(Boolean) : [];
      const previouslyAtStr = (formData.get('previouslyAt') as string)?.trim();
      const previouslyAt = previouslyAtStr ? previouslyAtStr.split(/[\n,]+/).map(s => s.trim()).filter(Boolean) : [];
      const showOnWebsite = (form.querySelector('[name="showOnWebsite"]') as HTMLInputElement)?.checked ?? true;
      await addDoc(collection(db, 'teamMembers'), {
        name: formData.get('name'),
        role: formData.get('role'),
        bio: formData.get('bio'),
        photoURL,
        creds,
        education,
        previouslyAt,
        email: (formData.get('email') as string)?.trim() || null,
        linkedin: (formData.get('linkedin') as string)?.trim() || null,
        order: teamMembers.length + 1,
        showOnWebsite,
      });

      setShowAddForm(false);
      loadData();
    } catch (error) {
      console.error('Error adding team member:', error);
    }
  };

  const handleUpdateTeamMember = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingMember) return;

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const existingPhoto = editingMember.photoURL;
      let photoURL: string | null =
        existingPhoto != null && String(existingPhoto).trim() !== '' ? String(existingPhoto) : null;
      const photoFile = formData.get('photo') as File;

      if (photoFile && photoFile.size > 0) {
        const photoRef = ref(storage, `team/${Date.now()}_${photoFile.name}`);
        await uploadBytes(photoRef, photoFile);
        photoURL = await getDownloadURL(photoRef);
      }

      const credsStr = (formData.get('creds') as string)?.trim();
      const creds = credsStr ? credsStr.split(/[\n,]+/).map(s => s.trim()).filter(Boolean) : [];
      const educationStr = (formData.get('education') as string)?.trim();
      const education = educationStr ? educationStr.split(/[\n,]+/).map(s => s.trim()).filter(Boolean) : [];
      const previouslyAtStr = (formData.get('previouslyAt') as string)?.trim();
      const previouslyAt = previouslyAtStr ? previouslyAtStr.split(/[\n,]+/).map(s => s.trim()).filter(Boolean) : [];
      const showOnWebsite = (form.querySelector('[name="showOnWebsite"]') as HTMLInputElement)?.checked ?? true;
      await updateDoc(doc(db, 'teamMembers', editingMember.id), {
        name: formData.get('name'),
        role: formData.get('role'),
        bio: formData.get('bio'),
        photoURL,
        creds,
        education,
        previouslyAt,
        email: (formData.get('email') as string)?.trim() || null,
        linkedin: (formData.get('linkedin') as string)?.trim() || null,
        showOnWebsite,
      });

      setEditingMember(null);
      loadData();
    } catch (error) {
      console.error('Error updating team member:', error);
    }
  };

  const handleSeedDefaultTeam = async () => {
    if (teamMembers.length > 0 && !window.confirm('Team members already exist. This will add the default team. Continue?')) return;
    setSeedingTeam(true);
    try {
      for (let i = 0; i < DEFAULT_TEAM.length; i++) {
        const m = DEFAULT_TEAM[i];
        await addDoc(collection(db, 'teamMembers'), {
          name: m.name,
          role: m.role,
          bio: m.bio,
          photoURL: m.photoURL,
          creds: m.creds || [],
          education: m.education || [],
          previouslyAt: m.previouslyAt || [],
          email: m.email || null,
          linkedin: m.linkedin || null,
          order: teamMembers.length + i + 1,
          showOnWebsite: true,
        });
      }
      loadData();
    } catch (error) {
      console.error('Error seeding team:', error);
      alert('Failed to seed team. Please try again.');
    } finally {
      setSeedingTeam(false);
    }
  };

  const handleDownloadInvestorCsvSample = () => {
    const sample = 'investor1@example.com\ninvestor2@example.com\ninvestor3@company.com';
    const blob = new Blob([sample], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'investor-whitelist-sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDeleteTeamMember = async (member: TeamMember) => {
    if (
      !adminDeleteConfirm(
        `Remove team member "${member.name}"? They will no longer appear on the public site if they were shown.\n\nThis cannot be undone.`
      )
    )
      return;
    setTeamDeletingId(member.id);
    try {
      await deleteDoc(doc(db, 'teamMembers', member.id));
      if (editingMember?.id === member.id) setEditingMember(null);
      await loadData();
    } catch (error) {
      console.error('Error deleting team member:', error);
      window.alert('Could not delete this team member. Try again or check the console.');
    } finally {
      setTeamDeletingId(null);
    }
  };

  const handleDeleteContact = async (contact: ContactInquiry) => {
    if (
      !adminDeleteConfirm(
        `Delete contact inquiry from "${contact.name}" (${contact.email})?\n\nThis cannot be undone.`
      )
    )
      return;
    setContactDeletingId(contact.id);
    try {
      await deleteDoc(doc(db, 'contactInquiries', contact.id));
      if (editingContact?.id === contact.id) setEditingContact(null);
      await loadData();
    } catch (error) {
      console.error('Error deleting contact:', error);
      window.alert('Could not delete this inquiry. Try again or check the console.');
    } finally {
      setContactDeletingId(null);
    }
  };

  const handleUpdateContactStatus = async (id: string, status: string) => {
    try {
      await updateDoc(doc(db, 'contactInquiries', id), { status });
      loadData();
    } catch (error) {
      console.error('Error updating contact status:', error);
    }
  };

  const handleUpdateContact = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingContact) return;
    const formData = new FormData(e.currentTarget);
    try {
      await updateDoc(doc(db, 'contactInquiries', editingContact.id), {
        name: formDataTrimmedString(formData, 'name'),
        email: formDataTrimmedString(formData, 'email'),
        phone: formDataTrimmedString(formData, 'phone'),
        message: formDataTrimmedString(formData, 'message'),
        organisation: formDataTrimmedString(formData, 'organisation') || null,
        status: formDataTrimmedString(formData, 'status') || 'new',
      });
      setEditingContact(null);
      loadData();
    } catch (error) {
      console.error('Error updating contact:', error);
      window.alert('Could not save changes. Check you are signed in and try again.');
    }
  };

  const handleUpdateDeckRequest = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingDeck) return;
    const formData = new FormData(e.currentTarget);
    const name = formDataTrimmedString(formData, 'name');
    const phone = formDataTrimmedString(formData, 'phone');
    const email = formDataTrimmedString(formData, 'email');
    const organisation = formDataTrimmedString(formData, 'organisation');
    const status = formDataTrimmedString(formData, 'status') || 'new';
    if (!name) {
      window.alert('Name is required.');
      return;
    }
    if (!isValidFundDeckPhone(phone)) {
      window.alert(FUND_DECK_PHONE_ERROR);
      return;
    }
    try {
      await updateDoc(doc(db, 'deckRequests', editingDeck.id), {
        name,
        phone,
        email: email || null,
        organisation: organisation || null,
        status,
      });
      setEditingDeck(null);
      loadData();
    } catch (error) {
      console.error('Error updating deck request:', error);
      window.alert('Could not save deck request. Check you are signed in and try again.');
    }
  };

  const handleUpdateCareer = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingCareer) return;
    const formData = new FormData(e.currentTarget);
    try {
      await updateDoc(doc(db, 'careerApplications', editingCareer.id), {
        name: formDataTrimmedString(formData, 'name'),
        email: formDataTrimmedString(formData, 'email'),
        phone: formDataTrimmedString(formData, 'phone'),
        role: formDataTrimmedString(formData, 'role') || null,
        status: formDataTrimmedString(formData, 'status') || 'new',
      });
      setEditingCareer(null);
      loadData();
    } catch (error) {
      console.error('Error updating career:', error);
      window.alert('Could not save changes. Check you are signed in and try again.');
    }
  };

  const handleDeleteCareer = async (career: CareerApplication) => {
    if (
      !adminDeleteConfirm(
        `Delete career application from "${career.name}" (${career.email})? The Firestore row is removed; any resume file in Storage is left in place unless you delete it manually in Storage.\n\nThis cannot be undone.`
      )
    )
      return;
    setCareerDeletingId(career.id);
    try {
      await deleteDoc(doc(db, 'careerApplications', career.id));
      if (editingCareer?.id === career.id) setEditingCareer(null);
      await loadData();
    } catch (error) {
      console.error('Error deleting career application:', error);
      window.alert('Could not delete this application. Try again or check the console.');
    } finally {
      setCareerDeletingId(null);
    }
  };

  const handleUpdateCareerStatus = async (id: string, status: string) => {
    try {
      await updateDoc(doc(db, 'careerApplications', id), { status });
      loadData();
    } catch (error) {
      console.error('Error updating career status:', error);
    }
  };

  const handleUpdateDeckStatus = async (id: string, status: string) => {
    try {
      await updateDoc(doc(db, 'deckRequests', id), { status });
      loadData();
    } catch (error) {
      console.error('Error updating deck request status:', error);
    }
  };

  const handleDeleteDeckRequest = async (req: DeckRequest) => {
    const summary =
      `Delete deck request for "${req.name}"?\n\n` +
      'This permanently removes the lead from the admin list and revokes every active fund-deck link that was issued for this request (saved URLs will stop working).\n\nThis cannot be undone.';
    if (!adminDeleteConfirm(summary)) return;
    setDeckDeletingId(req.id);
    try {
      const fn = httpsCallable(functions, 'deleteDeckRequestAdmin');
      await fn({ deckRequestId: req.id });
      setEditingDeck((cur) => (cur?.id === req.id ? null : cur));
      await loadData();
    } catch (error) {
      window.alert(userMessageFromCallableError(error));
    } finally {
      setDeckDeletingId(null);
    }
  };

  const handleUpdateGovernanceRequest = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingGov) return;
    const formData = new FormData(e.currentTarget);
    try {
      await updateDoc(doc(db, 'governanceRequests', editingGov.id), {
        name: formDataTrimmedString(formData, 'name'),
        phone: formDataTrimmedString(formData, 'phone'),
        email: formDataTrimmedString(formData, 'email') || null,
        organisation: formDataTrimmedString(formData, 'organisation') || null,
        status: formDataTrimmedString(formData, 'status') || 'new',
      });
      setEditingGov(null);
      loadData();
    } catch (error) {
      console.error('Error updating governance request:', error);
      window.alert('Could not save governance request. Check you are signed in and try again.');
    }
  };

  const handleUpdateGovernanceStatus = async (id: string, status: string) => {
    try {
      await updateDoc(doc(db, 'governanceRequests', id), { status });
      loadData();
    } catch (error) {
      console.error('Error updating governance request status:', error);
    }
  };

  const handleToggleTeamVisible = async (member: TeamMember) => {
    const visible = member.showOnWebsite !== false;
    try {
      await updateDoc(doc(db, 'teamMembers', member.id), { showOnWebsite: !visible });
      loadData();
    } catch (error) {
      console.error('Error toggling team visibility:', error);
    }
  };

  const formatTs = (t: { toDate?: () => Date } | null | undefined) =>
    t?.toDate ? t.toDate().toISOString() : '';

  const handleAddReport = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const file = formData.get('file') as File;
    if (!file || file.size === 0) {
      alert('Please select a PDF file');
      return;
    }
    try {
      const reportRef = ref(storage, `reports/${Date.now()}_${file.name}`);
      await uploadBytes(reportRef, file);
      const fileURL = await getDownloadURL(reportRef);
      await addDoc(collection(db, 'reports'), {
        title: formData.get('title') || file.name.replace('.pdf', ''),
        type: formData.get('type'),
        date: new Date((formData.get('date') as string) || Date.now()),
        fileURL,
        published: formData.get('published') === 'on',
      });
      setShowReportForm(false);
      loadData();
    } catch (error) {
      console.error('Error adding report:', error);
    }
  };

  const handleToggleReportPublish = async (report: Report) => {
    try {
      await updateDoc(doc(db, 'reports', report.id), { published: !report.published });
      loadData();
    } catch (error) {
      console.error('Error updating report:', error);
    }
  };

  const handleDeleteReport = async (id: string) => {
    if (adminDeleteConfirm('Delete this report?\n\nThis cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'reports', id));
        loadData();
      } catch (error) {
        console.error('Error deleting report:', error);
      }
    }
  };

  const handleSaveBlog = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isRichTextEmpty(blogContentHtml)) {
      alert('Please add article body content (write in the editor or paste your text).');
      return;
    }
    const formData = new FormData(e.currentTarget);
    let imageURL: string | undefined = editingBlog?.imageURL;
    const imageFile = blogImageInputRef.current?.files?.[0];
    if (imageFile && imageFile.size > 0) {
      setBlogImageUploading(true);
      try {
        const ext = imageFile.name.split('.').pop()?.toLowerCase() || 'jpg';
        const imageRef = ref(storage, `blog/${Date.now()}_${imageFile.name.replace(/\s/g, '_')}.${ext}`);
        await uploadBytes(imageRef, imageFile);
        imageURL = await getDownloadURL(imageRef);
      } catch (err) {
        console.error('Error uploading blog image:', err);
        alert('Image upload failed. Save without image or try again.');
      }
      setBlogImageUploading(false);
    }
    const title = formDataTrimmedString(formData, 'title');
    const slugField = formDataTrimmedString(formData, 'slug');
    const baseSlug = slugField || title;
    const slug = await ensureUniqueInsightSlug(baseSlug, editingBlog?.id);

    const data: Record<string, unknown> = {
      title: formData.get('title'),
      excerpt: formData.get('excerpt'),
      content: sanitizeBlogHtml(blogContentHtml),
      slug,
      date: editingBlog?.date ? editingBlog.date : new Date(),
      published: formData.get('published') === 'on',
    };
    if (imageURL !== undefined) data.imageURL = imageURL;
    try {
      if (editingBlog) {
        await updateDoc(doc(db, 'blogPosts', editingBlog.id), data);
        setEditingBlog(null);
      } else {
        await addDoc(collection(db, 'blogPosts'), data);
      }
      setShowBlogForm(false);
      if (blogImageInputRef.current) blogImageInputRef.current.value = '';
      loadData();
    } catch (error) {
      console.error('Error saving blog post:', error);
    }
  };

  const handleToggleBlogPublish = async (post: BlogPost) => {
    try {
      await updateDoc(doc(db, 'blogPosts', post.id), { published: !post.published });
      loadData();
    } catch (error) {
      console.error('Error updating blog:', error);
    }
  };

  const handleDeleteBlog = async (id: string) => {
    if (adminDeleteConfirm('Delete this blog post?\n\nThis cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'blogPosts', id));
        loadData();
      } catch (error) {
        console.error('Error deleting blog:', error);
      }
    }
  };

  const parseCsvEmails = (text: string): string[] => {
    const lines = text.split(/[\r\n]+/).map(l => l.trim()).filter(Boolean);
    const emails = new Set<string>();
    for (const line of lines) {
      const parts = line.split(/[,;\t]/).map(p => p.trim().toLowerCase()).filter(Boolean);
      for (const p of parts) {
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p)) emails.add(p);
      }
    }
    return Array.from(emails).sort();
  };

  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvUploading(true);
    try {
      const text = await file.text();
      const newEmails = parseCsvEmails(text);
      const merged = Array.from(new Set([...investorEmails, ...newEmails])).sort();
      await setDoc(doc(db, 'investorEmails', 'whitelist'), { emails: merged });
      setInvestorEmails(merged);
    } catch (error) {
      console.error('Error uploading CSV:', error);
      alert('Failed to upload CSV. Please check the format.');
    } finally {
      setCsvUploading(false);
      e.target.value = '';
    }
  };

  const handleReplaceWhitelist = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !window.confirm('This will replace the entire whitelist. Continue?')) return;
    setCsvUploading(true);
    try {
      const text = await file.text();
      const emails = parseCsvEmails(text);
      await setDoc(doc(db, 'investorEmails', 'whitelist'), { emails });
      setInvestorEmails(emails);
    } catch (error) {
      console.error('Error replacing whitelist:', error);
      alert('Failed to upload CSV.');
    } finally {
      setCsvUploading(false);
      e.target.value = '';
    }
  };

  const runRebuildFundDeckPages = useCallback(
    async (showAlert: boolean): Promise<boolean> => {
      if (!getAuth().currentUser) {
        if (showAlert) alert('You must be logged in as admin.');
        return false;
      }
      setRebuildingDeckPages(true);
      try {
        const fn = httpsCallable(functions, 'rebuildFundDeckPages');
        await fn();
        await refreshSampleDeckPanel();
        setDeckPreviewRefreshKey((k) => k + 1);
        if (showAlert) alert('Deck images rebuilt successfully.');
        return true;
      } catch (error) {
        console.error('rebuildFundDeckPages:', error);
        if (showAlert) alert(userMessageFromCallableError(error));
        await refreshSampleDeckPanel();
        setDeckPreviewRefreshKey((k) => k + 1);
        return false;
      } finally {
        setRebuildingDeckPages(false);
      }
    },
    [refreshSampleDeckPanel]
  );

  const handleSampleDeckUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      alert('Please select a PDF file');
      return;
    }
    setSampleDeckUploading(true);
    try {
      const deckRef = ref(storage, FUND_DECK_PDF_STORAGE_PATH);
      await uploadBytes(deckRef, file);
      const fileURL = await getDownloadURL(deckRef);
      try {
        await deleteObject(ref(storage, FUND_DECK_PDF_LEGACY_STORAGE_PATH));
      } catch {
        /* no legacy PDF */
      }
      await setDoc(
        doc(db, 'siteConfig', 'sampleDeck'),
        {
          fileURL,
          updatedAt: new Date(),
          processingStatus: 'processing',
          processingError: deleteField(),
          pageUrls: deleteField(),
          pageImagePaths: [],
          pageCount: 0,
        },
        { merge: true }
      );
      setSampleDeckUrl(fileURL);
      setSampleDeckProcessingStatus('processing');
      setSampleDeckPageCount(0);
      setSampleDeckProcessingError(null);
      const rebuilt = await runRebuildFundDeckPages(false);
      if (!rebuilt) {
        alert('PDF uploaded. Click “Regenerate WebP pages” if the deck viewer stays on “processing”.');
      }
      await refreshSampleDeckPanel();
      setDeckPreviewRefreshKey((k) => k + 1);
    } catch (error) {
      console.error('Error uploading fund deck PDF:', error);
      alert('Failed to upload or process deck. Try again or use “Regenerate WebP pages”.');
    } finally {
      setSampleDeckUploading(false);
      e.target.value = '';
    }
  };

  const handleSaveDeal = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const data = {
      type: fd.get('type') as string,
      typeClass: (fd.get('typeClass') as Deal['typeClass']) || 'ipo',
      company: fd.get('company') as string,
      sector: fd.get('sector') as string,
      amount: fd.get('amount') as string,
      amountLabel: fd.get('amountLabel') as string,
      desc: fd.get('desc') as string,
      date: fd.get('date') as string,
      order: editingDeal?.order ?? deals.length,
    };
    try {
      if (editingDeal) {
        await updateDoc(doc(db, 'deals', editingDeal.id), data);
      } else {
        const nextOrder = deals.reduce((max, d) => Math.max(max, d.order ?? 0), -1) + 1;
        await addDoc(collection(db, 'deals'), { ...data, order: nextOrder });
      }
      setEditingDeal(null);
      setShowDealForm(false);
      loadData();
    } catch (err) {
      console.error(err);
      alert('Failed to save deal.');
    }
  };

  const [dealReordering, setDealReordering] = useState(false);

  /** Moves an item one slot up/down and rewrites `order` as 0..n-1 for the whole list (also heals gaps/duplicates). */
  const moveOrderedItem = async <T extends { id: string; order: number }>(
    collectionName: 'deals' | 'dealGalleries',
    items: T[],
    setItems: (items: T[]) => void,
    index: number,
    direction: -1 | 1
  ) => {
    const target = index + direction;
    if (target < 0 || target >= items.length || dealReordering) return;
    const reordered = [...items];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    const renumbered = reordered.map((item, i) => ({ ...item, order: i }));
    setItems(renumbered);
    setDealReordering(true);
    try {
      const batch = writeBatch(db);
      renumbered.forEach((item) => batch.update(doc(db, collectionName, item.id), { order: item.order }));
      await batch.commit();
    } catch (err) {
      console.error(err);
      alert('Failed to save the new order.');
      loadData();
    } finally {
      setDealReordering(false);
    }
  };

  const handleMoveDeal = (index: number, direction: -1 | 1) =>
    moveOrderedItem('deals', deals, setDeals, index, direction);

  const handleMoveGallery = (index: number, direction: -1 | 1) =>
    moveOrderedItem('dealGalleries', dealGalleries, setDealGalleries, index, direction);

  const handleDeleteDeal = async (id: string) => {
    if (!adminDeleteConfirm('Delete this deal?\n\nThis cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'deals', id));
      loadData();
    } catch (err) {
      console.error(err);
      alert('Failed to delete.');
    }
  };

  const handleSeedDeals = async () => {
    const defaultDeals: Omit<Deal, 'id'>[] = [
      { type: 'PRE-IPO FUNDRAISE', typeClass: 'preipo', company: 'The Hive Hostels (Colstay Limited)', sector: 'Luxury Student Housing & Co-Living', amount: '₹11.5 Cr', amountLabel: 'Pre-IPO Round Raised', desc: 'Acted as Corporate Advisor to Colstay Private Limited, operator of The Hive Hostels brand. Handholded the company through investor identification, pitch structuring and round closure. Marquee investors: Dr. Shriram Madhav Nene, Sahil Vachani (MD, Max Estates), Anchorage Capital Fund & Sixth Sense Ventures.', date: 'Aug 2024', order: 0 },
      { type: 'PRE-IPO FUNDRAISE', typeClass: 'preipo', company: 'Bhadora Industries Limited', sector: 'Electrical Cables & Infrastructure', amount: '₹5 Cr', amountLabel: 'Pre-IPO Round Raised', desc: 'Served as Corporate Advisor to Bhadora Industries Limited, guiding the company through its pre-IPO capital raise. Responsible for investor identification, transaction structuring and selection of intermediaries as part of the broader IPO readiness mandate.', date: 'Dec 2024', order: 1 },
      { type: 'IPO', typeClass: 'ipo', company: 'Bhadora Industries Limited', sector: 'Electrical Cables & Infrastructure', amount: '₹56 Cr', amountLabel: 'IPO Issue Size', desc: 'Following the pre-IPO round, continued as Corporate Advisor through the full IPO journey – coordinating selection of merchant banker, registrar and legal counsel, supporting investor roadshows and handholding management through SEBI filing to listing day. Kyro team has presented speech at NSE listing ceremony.', date: 'Aug 2025', order: 2 },
      { type: 'IPO', typeClass: 'ipo', company: 'EPW India Limited', sector: 'IT Electronics Refurbishing', amount: '₹32 Cr', amountLabel: 'IPO Issue Size', desc: 'Served as Corporate Advisor through the entire IPO lifecycle – from pre-IPO readiness to merchant banker selection, investor roadshows and listing. Listed on NSE, opened at 111-14.4% premium. Revenue +188% & PAT +485% YoY. Kyro team has presented speech at NSE listing ceremony.', date: 'Dec 2025', order: 3 },
      { type: 'DEBT SYNDICATION', typeClass: 'debt', company: 'Keemaya Resorts & Spas LLP', sector: 'Hospitality & Leisure', amount: '₹50 Cr', amountLabel: 'Issue Size', desc: 'Acted as Corporate Advisor for a ₹50 Crore debt round for Keemaya Resorts & Spas LLP. Responsible for lender identification, term sheet negotiation, intermediary selection and end-to-end handholding of the hospitality group through successful deal closure.', date: 'Jan 2025', order: 4 },
    ];
    if (!window.confirm(`Add ${defaultDeals.length} default deals? This will replace any existing deals.`)) return;
    try {
      const existing = await getDocs(collection(db, 'deals'));
      for (const d of existing.docs) await deleteDoc(doc(db, 'deals', d.id));
      for (let i = 0; i < defaultDeals.length; i++) {
        await addDoc(collection(db, 'deals'), { ...defaultDeals[i], order: i });
      }
      loadData();
      alert('Default deals added.');
    } catch (err) {
      console.error(err);
      alert('Failed to seed deals.');
    }
  };

  const handleSaveGallery = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const slugRaw = (fd.get('slug') as string)?.trim().toLowerCase().replace(/\s+/g, '-') || '';
    const slug = slugRaw || 'gallery';
    const title = (fd.get('title') as string)?.trim() || '';
    const caption = (fd.get('caption') as string)?.trim() || '';
    const urls = editingGallery?.imageURLs || [];
    const orderField = fd.get('order');
    let order =
      orderField !== null && String(orderField).trim() !== ''
        ? parseInt(String(orderField), 10)
        : editingGallery?.id && editingGallery.id !== 'new'
          ? editingGallery.order
          : dealGalleries.length;
    if (Number.isNaN(order) || order < 0) order = dealGalleries.length;

    const existingId = editingGallery?.id && editingGallery.id !== 'new' ? editingGallery.id : null;
    const slugTaken = dealGalleries.some((g) => g.slug === slug && g.id !== existingId);
    if (slugTaken) {
      alert('Another gallery already uses this slug. Choose a unique slug (e.g. epw, bhadora).');
      return;
    }

    try {
      if (existingId) {
        await updateDoc(doc(db, 'dealGalleries', existingId), { slug, title, caption, imageURLs: urls, order });
      } else {
        await addDoc(collection(db, 'dealGalleries'), { slug, title, caption, imageURLs: urls, order });
      }
      setEditingGallery(null);
      loadData();
    } catch (err) {
      console.error(err);
      alert('Failed to save gallery.');
    }
  };

  const handleGalleryImageUpload = async (galleryId: string, file: File) => {
    const g = dealGalleries.find((x) => x.id === galleryId);
    if (!g) return;
    if (!g.slug || !String(g.slug).trim()) {
      alert('Set and save a gallery slug before uploading images.');
      return;
    }
    const folderSlug = String(g.slug).trim().toLowerCase().replace(/\s+/g, '-');
    setGalleryImageUploading(galleryId);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `deal-gallery/${folderSlug}/${Date.now()}.${ext}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const urls = [...(g.imageURLs || []), url];
      await updateDoc(doc(db, 'dealGalleries', galleryId), { imageURLs: urls });
      loadData();
    } catch (err) {
      console.error(err);
      alert('Failed to upload image.');
    } finally {
      setGalleryImageUploading(null);
    }
  };

  const handleRemoveGalleryImage = async (galleryId: string, index: number) => {
    const g = dealGalleries.find((x) => x.id === galleryId);
    if (!g || !g.imageURLs?.[index]) return;
    if (
      !adminDeleteConfirm(
        'Remove this image from the gallery? (File stays in Storage.)\n\nThis cannot be undone.'
      )
    )
      return;
    const urls = g.imageURLs.filter((_, i) => i !== index);
    try {
      await updateDoc(doc(db, 'dealGalleries', galleryId), { imageURLs: urls });
      loadData();
    } catch (err) {
      console.error(err);
      alert('Failed to remove.');
    }
  };

  const handleDeleteGallery = async (id: string, label: string) => {
    if (
      !adminDeleteConfirm(
        `Delete gallery "${label}"? Firestore entry is removed; image files remain in Storage until cleaned up manually.\n\nThis cannot be undone.`
      )
    )
      return;
    try {
      await deleteDoc(doc(db, 'dealGalleries', id));
      if (editingGallery?.id === id) setEditingGallery(null);
      loadData();
    } catch (err) {
      console.error(err);
      alert('Failed to delete gallery.');
    }
  };

  const handleAddSingleEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      alert('Please enter a valid email address.');
      return;
    }
    if (investorEmails.includes(trimmed)) {
      alert('This email is already in the whitelist.');
      return;
    }
    setAddingEmail(true);
    try {
      const updated = [...investorEmails, trimmed].sort();
      await setDoc(doc(db, 'investorEmails', 'whitelist'), { emails: updated });
      setInvestorEmails(updated);
      setNewEmail('');
    } catch (error) {
      console.error('Error adding email:', error);
      alert('Failed to add email.');
    } finally {
      setAddingEmail(false);
    }
  };

  const handleEditInvestorEmail = (email: string) => {
    setEditingInvestorEmail(email);
    setEditingInvestorValue(email);
  };

  const handleSaveInvestorEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingInvestorEmail) return;
    const trimmed = editingInvestorValue.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      alert('Please enter a valid email address.');
      return;
    }
    if (trimmed !== editingInvestorEmail && investorEmails.includes(trimmed)) {
      alert('This email is already in the whitelist.');
      return;
    }
    try {
      const updated = investorEmails.map(e => e === editingInvestorEmail ? trimmed : e).sort();
      await setDoc(doc(db, 'investorEmails', 'whitelist'), { emails: updated });
      setInvestorEmails(updated);
      setEditingInvestorEmail(null);
      setEditingInvestorValue('');
    } catch (error) {
      console.error('Error updating email:', error);
      alert('Failed to update email.');
    }
  };

  const handleUpdateReport = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingReport) return;
    const formData = new FormData(e.currentTarget);
    try {
      await updateDoc(doc(db, 'reports', editingReport.id), {
        title: formData.get('title'),
        type: formData.get('type'),
        date: new Date((formData.get('date') as string) || editingReport.date?.toDate?.()?.toISOString?.() || Date.now()),
        published: formData.get('published') === 'on',
      });
      setEditingReport(null);
      loadData();
    } catch (error) {
      console.error('Error updating report:', error);
    }
  };

  const handleSaveHomeStats = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    try {
      await setDoc(doc(db, 'siteConfig', 'homeStats'), {
        targetIRR: (form.elements.namedItem('targetIRR') as HTMLInputElement).value,
        minInvestment: (form.elements.namedItem('minInvestment') as HTMLInputElement).value,
        transactionsAdvised: (form.elements.namedItem('transactionsAdvised') as HTMLInputElement).value,
        yearsAsianMarkets: (form.elements.namedItem('yearsAsianMarkets') as HTMLInputElement).value,
        updatedAt: new Date(),
      });
      setHomeStats({
        targetIRR: (form.elements.namedItem('targetIRR') as HTMLInputElement).value,
        minInvestment: (form.elements.namedItem('minInvestment') as HTMLInputElement).value,
        transactionsAdvised: (form.elements.namedItem('transactionsAdvised') as HTMLInputElement).value,
        yearsAsianMarkets: (form.elements.namedItem('yearsAsianMarkets') as HTMLInputElement).value,
      });
      alert('Home stats saved.');
    } catch (err) {
      console.error(err);
      alert('Failed to save.');
    }
  };

  const handleRemoveInvestorEmail = async (email: string) => {
    if (!adminDeleteConfirm(`Remove ${email} from the investor whitelist?\n\nThis cannot be undone.`)) return;
    const updated = investorEmails.filter(e => e !== email);
    await setDoc(doc(db, 'investorEmails', 'whitelist'), { emails: updated });
    setInvestorEmails(updated);
  };

  const deckFilterDateFrom = requestListPaging.deck.dateFrom;
  const deckFilterDateTo = requestListPaging.deck.dateTo;
  const filteredDeckRequests = useMemo(() => {
    const q = deckSearch.trim().toLowerCase();
    return deckRequests.filter((r) => {
      if (deckFilterStatus !== 'all' && (r.status || 'new') !== deckFilterStatus) return false;
      if (!matchesSubmittedDateRange(timestampFieldToMs(r.timestamp), deckFilterDateFrom, deckFilterDateTo)) return false;
      if (!q) return true;
      const hay = [r.name, r.email, r.phone, r.organisation].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [deckRequests, deckFilterStatus, deckSearch, deckFilterDateFrom, deckFilterDateTo]);

  const governanceDocKeys = useMemo(() => {
    const s = new Set<string>();
    governanceRequests.forEach((r) => {
      if (r.documentKey) s.add(r.documentKey);
    });
    return Array.from(s).sort();
  }, [governanceRequests]);

  const govFilterDateFrom = requestListPaging.governance.dateFrom;
  const govFilterDateTo = requestListPaging.governance.dateTo;
  const filteredGovernanceRequests = useMemo(() => {
    const q = govSearch.trim().toLowerCase();
    return governanceRequests.filter((r) => {
      if (govFilterStatus !== 'all' && (r.status || 'new') !== govFilterStatus) return false;
      if (govFilterDoc !== 'all' && r.documentKey !== govFilterDoc) return false;
      if (!matchesSubmittedDateRange(timestampFieldToMs(r.timestamp), govFilterDateFrom, govFilterDateTo)) return false;
      if (!q) return true;
      const hay = [r.name, r.email, r.phone, r.organisation, r.documentLabel].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [governanceRequests, govFilterStatus, govFilterDoc, govSearch, govFilterDateFrom, govFilterDateTo]);

  const contactFilterDateFrom = requestListPaging.contacts.dateFrom;
  const contactFilterDateTo = requestListPaging.contacts.dateTo;
  const filteredContacts = useMemo(() => {
    const q = contactSearch.trim().toLowerCase();
    return contacts.filter((c) => {
      if (contactFilterStatus !== 'all' && (c.status || 'new') !== contactFilterStatus) return false;
      if (!matchesSubmittedDateRange(timestampFieldToMs(c.timestamp), contactFilterDateFrom, contactFilterDateTo)) return false;
      if (!q) return true;
      const hay = [c.name, c.email, c.phone, c.organisation, c.message].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [contacts, contactFilterStatus, contactSearch, contactFilterDateFrom, contactFilterDateTo]);

  const careerFilterDateFrom = requestListPaging.careers.dateFrom;
  const careerFilterDateTo = requestListPaging.careers.dateTo;
  const filteredCareers = useMemo(() => {
    const q = careerSearch.trim().toLowerCase();
    return careers.filter((c) => {
      if (careerFilterStatus !== 'all' && (c.status || 'new') !== careerFilterStatus) return false;
      if (!matchesSubmittedDateRange(timestampFieldToMs(c.timestamp), careerFilterDateFrom, careerFilterDateTo)) return false;
      if (!q) return true;
      const hay = [c.name, c.email, c.phone, c.role].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [careers, careerFilterStatus, careerSearch, careerFilterDateFrom, careerFilterDateTo]);

  const contactsPaging = requestListPaging.contacts;
  const contactsTotalPages = Math.max(1, Math.ceil(filteredContacts.length / contactsPaging.pageSize) || 1);
  const contactsPage = Math.min(Math.max(1, contactsPaging.page), contactsTotalPages);
  const pagedContacts = useMemo(() => {
    const start = (contactsPage - 1) * contactsPaging.pageSize;
    return filteredContacts.slice(start, start + contactsPaging.pageSize);
  }, [filteredContacts, contactsPage, contactsPaging.pageSize]);

  const careersPaging = requestListPaging.careers;
  const careersTotalPages = Math.max(1, Math.ceil(filteredCareers.length / careersPaging.pageSize) || 1);
  const careersPage = Math.min(Math.max(1, careersPaging.page), careersTotalPages);
  const pagedCareers = useMemo(() => {
    const start = (careersPage - 1) * careersPaging.pageSize;
    return filteredCareers.slice(start, start + careersPaging.pageSize);
  }, [filteredCareers, careersPage, careersPaging.pageSize]);

  const deckListPaging = requestListPaging.deck;
  const deckListTotalPages = Math.max(1, Math.ceil(filteredDeckRequests.length / deckListPaging.pageSize) || 1);
  const deckListPage = Math.min(Math.max(1, deckListPaging.page), deckListTotalPages);
  const pagedDeckRequests = useMemo(() => {
    const start = (deckListPage - 1) * deckListPaging.pageSize;
    return filteredDeckRequests.slice(start, start + deckListPaging.pageSize);
  }, [filteredDeckRequests, deckListPage, deckListPaging.pageSize]);

  const govPaging = requestListPaging.governance;
  const govTotalPages = Math.max(1, Math.ceil(filteredGovernanceRequests.length / govPaging.pageSize) || 1);
  const govPage = Math.min(Math.max(1, govPaging.page), govTotalPages);
  const pagedGovernanceRequests = useMemo(() => {
    const start = (govPage - 1) * govPaging.pageSize;
    return filteredGovernanceRequests.slice(start, start + govPaging.pageSize);
  }, [filteredGovernanceRequests, govPage, govPaging.pageSize]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(filteredContacts.length / contactsPaging.pageSize));
    if (contactsPaging.page > tp) patchRequestListPaging('contacts', { page: tp });
  }, [filteredContacts.length, contactsPaging.page, contactsPaging.pageSize, patchRequestListPaging]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(filteredCareers.length / careersPaging.pageSize));
    if (careersPaging.page > tp) patchRequestListPaging('careers', { page: tp });
  }, [filteredCareers.length, careersPaging.page, careersPaging.pageSize, patchRequestListPaging]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(filteredDeckRequests.length / deckListPaging.pageSize));
    if (deckListPaging.page > tp) patchRequestListPaging('deck', { page: tp });
  }, [filteredDeckRequests.length, deckListPaging.page, deckListPaging.pageSize, patchRequestListPaging]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(filteredGovernanceRequests.length / govPaging.pageSize));
    if (govPaging.page > tp) patchRequestListPaging('governance', { page: tp });
  }, [filteredGovernanceRequests.length, govPaging.page, govPaging.pageSize, patchRequestListPaging]);

  if (loading) {
    return <Loader fullScreen size="large" />;
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <h1>Admin Dashboard</h1>
            <button onClick={handleLogout} className="logout-btn">
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="container">
          <div className="dashboard-tabs">
            <button
              className={`tab-btn ${activeTab === 'team' ? 'active' : ''}`}
              onClick={() => setActiveTab('team')}
            >
              <FaUsers /> Team Members ({teamMembers.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'contacts' ? 'active' : ''}`}
              onClick={() => setActiveTab('contacts')}
            >
              <FaEnvelope /> Contact Inquiries ({contacts.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'careers' ? 'active' : ''}`}
              onClick={() => setActiveTab('careers')}
            >
              <FaBriefcase /> Career Applications ({careers.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'deck' ? 'active' : ''}`}
              onClick={() => setActiveTab('deck')}
            >
              <FaFilePdf /> Deck ({deckRequests.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'governance' ? 'active' : ''}`}
              onClick={() => setActiveTab('governance')}
            >
              <FaFileAlt /> Governance Requests ({governanceRequests.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'reports' ? 'active' : ''}`}
              onClick={() => setActiveTab('reports')}
            >
              <FaFileUpload /> Reports ({reports.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'blog' ? 'active' : ''}`}
              onClick={() => setActiveTab('blog')}
            >
              <FaNewspaper /> Blog ({blogPosts.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'investors' ? 'active' : ''}`}
              onClick={() => setActiveTab('investors')}
            >
              <FaUserCheck /> Investors ({investorEmails.length})
            </button>
            <button
              className={`tab-btn ${activeTab === 'content' ? 'active' : ''}`}
              onClick={() => setActiveTab('content')}
            >
              <FaFilePdf /> Content
            </button>
            <button
              className={`tab-btn ${activeTab === 'deals' ? 'active' : ''}`}
              onClick={() => setActiveTab('deals')}
            >
              <FaImage /> Deals
            </button>
          </div>

          <div className="dashboard-panel">
            {activeTab === 'team' && (
              <div className="team-panel">
                <div className="panel-header">
                  <h2>Team Members</h2>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {teamMembers.length > 0 && (
                      <>
                        <button onClick={() => setShowAddForm(true)} className="add-btn">
                          <FaPlus /> Add Member
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {showAddForm && (
                  <div className="inline-form-container">
                    <form onSubmit={handleAddTeamMember} className="member-form-inline">
                      <h3>Add Team Member</h3>
                      <input type="text" name="name" placeholder="Full Name" required />
                      <input type="text" name="role" placeholder="Role/Position" required />
                      <textarea name="bio" placeholder="Bio" rows={4} required />
                      <textarea name="creds" placeholder="Credentials (About page badges, one per line or comma-separated)" rows={2} />
                      <textarea name="education" placeholder="Education (Home fund manager section, one per line or comma-separated)" rows={2} />
                      <textarea name="previouslyAt" placeholder="Previously at companies (e.g. Goldman Sachs, JPMorgan Chase, NOMURA)" rows={1} />
                      <input type="email" name="email" placeholder="Email (optional)" />
                      <input type="url" name="linkedin" placeholder="LinkedIn URL (optional)" />
                      <input type="file" name="photo" accept="image/*" />
                      <label className="admin-checkbox-label">
                        <input type="checkbox" name="showOnWebsite" defaultChecked /> Show on public website (Home / About)
                      </label>
                      <div className="form-actions">
                        <button type="submit" className="save-btn">Save</button>
                        <button type="button" onClick={() => setShowAddForm(false)} className="cancel-btn">Cancel</button>
                      </div>
                    </form>
                  </div>
                )}

                {teamMembers.length === 0 ? (
                  <div className="empty-state-admin">
                    <p>No team members added yet.</p>
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <button onClick={handleSeedDefaultTeam} className="add-btn" disabled={seedingTeam}>
                        {seedingTeam ? 'Seeding...' : 'Seed Default Team'}
                      </button>
                      <button onClick={() => setShowAddForm(true)} className="add-btn">
                        <FaPlus /> Add Manually
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="team-list">
                    {teamMembers.map((member) => (
                      <div key={member.id} className="team-item">
                        {editingMember?.id === member.id ? (
                          <form
                            key={member.id}
                            onSubmit={handleUpdateTeamMember}
                            className="member-form-inline team-member-edit-form"
                          >
                            <h3>Edit Team Member</h3>
                            <input type="text" name="name" defaultValue={member.name} placeholder="Full Name" required />
                            <input type="text" name="role" defaultValue={member.role} placeholder="Role/Position" required />
                            <textarea name="bio" defaultValue={member.bio} placeholder="Bio" rows={4} required />
                            <textarea
                              name="creds"
                              placeholder="Credentials (About page badges)"
                              rows={2}
                              defaultValue={member.creds?.join('\n')}
                            />
                            <textarea
                              name="education"
                              placeholder="Education (Home fund manager)"
                              rows={2}
                              defaultValue={member.education?.join('\n')}
                            />
                            <textarea
                              name="previouslyAt"
                              placeholder="Previously at companies"
                              rows={1}
                              defaultValue={member.previouslyAt?.join(', ')}
                            />
                            <input type="email" name="email" placeholder="Email (optional)" defaultValue={member.email ?? ''} />
                            <input type="url" name="linkedin" placeholder="LinkedIn URL (optional)" defaultValue={member.linkedin ?? ''} />
                            <input type="file" name="photo" accept="image/*" />
                            <label className="admin-checkbox-label">
                              <input
                                type="checkbox"
                                name="showOnWebsite"
                                defaultChecked={member.showOnWebsite !== false}
                              />{' '}
                              Show on public website (Home / About)
                            </label>
                            <div className="form-actions">
                              <button type="submit" className="save-btn">
                                Update
                              </button>
                              <button type="button" onClick={() => setEditingMember(null)} className="cancel-btn">
                                Cancel
                              </button>
                            </div>
                          </form>
                        ) : (
                          <>
                            {member.photoURL && <img src={member.photoURL} alt={member.name} />}
                            <div className="team-info">
                              <h3>{member.name}</h3>
                              <p className="role">{member.role}</p>
                              <p className="bio">{member.bio}</p>
                            </div>
                            <div className="team-actions">
                              <label className="admin-toggle-inline" title="Visible on Home and About team sections">
                                <input
                                  type="checkbox"
                                  checked={member.showOnWebsite !== false}
                                  onChange={() => handleToggleTeamVisible(member)}
                                />
                                <span>On website</span>
                              </label>
                              <button
                                type="button"
                                onClick={() => {
                                  setShowAddForm(false);
                                  setEditingMember(member);
                                }}
                                className="edit-btn"
                              >
                                <FaEdit /> Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => void handleDeleteTeamMember(member)}
                                className="delete-btn"
                                disabled={teamDeletingId === member.id}
                                aria-label={`Delete team member ${member.name}`}
                              >
                                <FaTrash /> {teamDeletingId === member.id ? '…' : 'Delete'}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'contacts' && (
              <div className="contacts-panel">
                <div className="panel-header">
                  <h2>Contact Inquiries</h2>
                </div>
                {contacts.length > 0 && (
                  <div className="admin-list-toolbar">
                    <select
                      className="status-select"
                      value={contactFilterStatus}
                      onChange={(e) => {
                        setContactFilterStatus(e.target.value);
                        patchRequestListPaging('contacts', { page: 1 });
                      }}
                      aria-label="Filter by status"
                    >
                      <option value="all">All statuses</option>
                      <option value="new">New</option>
                      <option value="in-progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                    </select>
                    <AdminRequestDateFilters
                      dateFrom={contactsPaging.dateFrom}
                      dateTo={contactsPaging.dateTo}
                      pageSize={contactsPaging.pageSize}
                      onDateFrom={(v) => patchRequestListPaging('contacts', { dateFrom: v, page: 1 })}
                      onDateTo={(v) => patchRequestListPaging('contacts', { dateTo: v, page: 1 })}
                      onPageSize={(n) => patchRequestListPaging('contacts', { pageSize: n, page: 1 })}
                    />
                    <input
                      type="search"
                      className="admin-filter-search"
                      placeholder="Search name, email, phone, message…"
                      value={contactSearch}
                      onChange={(e) => {
                        setContactSearch(e.target.value);
                        patchRequestListPaging('contacts', { page: 1 });
                      }}
                      aria-label="Search contacts"
                    />
                    <button
                      type="button"
                      className="add-btn admin-export-btn"
                      onClick={() =>
                        downloadCsv(
                          'contact-inquiries.csv',
                          ['timestamp', 'status', 'name', 'email', 'phone', 'organisation', 'message'],
                          filteredContacts.map((c) => [
                            formatTs(c.timestamp),
                            c.status || 'new',
                            c.name,
                            c.email,
                            c.phone || '',
                            c.organisation || '',
                            (c.message || '').replace(/\r?\n/g, ' '),
                          ])
                        )
                      }
                    >
                      <FaDownload /> Export CSV ({filteredContacts.length})
                    </button>
                  </div>
                )}
                {contacts.length === 0 ? (
                  <div className="empty-state-admin">
                    <p>No contact inquiries yet.</p>
                  </div>
                ) : filteredContacts.length === 0 ? (
                  <div className="empty-state-admin">
                    <p>No inquiries match your filters.</p>
                  </div>
                ) : (
                  <div className="inquiries-list">
                    {pagedContacts.map((contact) => (
                      <div key={contact.id} className="inquiry-item">
                        {editingContact?.id === contact.id ? (
                          <form onSubmit={handleUpdateContact} className="member-form-inline" style={{ margin: 0 }}>
                            <h3>Edit Contact</h3>
                            <input type="text" name="name" defaultValue={contact.name} placeholder="Name" required />
                            <input type="email" name="email" defaultValue={contact.email} placeholder="Email" required />
                            <input type="tel" name="phone" defaultValue={contact.phone} placeholder="Phone" />
                            <input type="text" name="organisation" defaultValue={contact.organisation || ''} placeholder="Organisation" />
                            <textarea name="message" defaultValue={contact.message} placeholder="Message" rows={3} />
                            <select name="status" defaultValue={contact.status || 'new'}>
                              <option value="new">New</option>
                              <option value="in-progress">In Progress</option>
                              <option value="resolved">Resolved</option>
                            </select>
                            <div className="form-actions">
                              <button type="submit" className="save-btn">Save</button>
                              <button type="button" onClick={() => setEditingContact(null)} className="cancel-btn">Cancel</button>
                            </div>
                          </form>
                        ) : (
                          <>
                            <div className="inquiry-header">
                              <h3>{contact.name}</h3>
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <select
                                  value={contact.status || 'new'}
                                  onChange={(e) => handleUpdateContactStatus(contact.id, e.target.value)}
                                  className="status-select"
                                >
                                  <option value="new">New</option>
                                  <option value="in-progress">In Progress</option>
                                  <option value="resolved">Resolved</option>
                                </select>
                                <button onClick={() => setEditingContact(contact)} className="edit-btn" style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}><FaEdit /> Edit</button>
                                <button
                                  type="button"
                                  onClick={() => void handleDeleteContact(contact)}
                                  className="delete-btn"
                                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                                  disabled={contactDeletingId === contact.id}
                                  aria-label={`Delete contact inquiry from ${contact.name}`}
                                >
                                  <FaTrash /> {contactDeletingId === contact.id ? '…' : 'Delete'}
                                </button>
                              </div>
                            </div>
                            <p><strong>Email:</strong> {contact.email}</p>
                            <p><strong>Phone:</strong> {contact.phone}</p>
                            {contact.organisation && <p><strong>Organisation:</strong> {contact.organisation}</p>}
                            <p><strong>Message:</strong> {contact.message}</p>
                            <p className="timestamp">{contact.timestamp?.toDate().toLocaleString()}</p>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {contacts.length > 0 && filteredContacts.length > 0 && (
                  <AdminRequestPaginationRow
                    page={contactsPage}
                    totalPages={contactsTotalPages}
                    totalFiltered={filteredContacts.length}
                    onPrev={() => patchRequestListPaging('contacts', { page: contactsPage - 1 })}
                    onNext={() => patchRequestListPaging('contacts', { page: contactsPage + 1 })}
                  />
                )}
              </div>
            )}

            {activeTab === 'careers' && (
              <div className="careers-panel">
                <div className="panel-header">
                  <h2>Career Applications</h2>
                </div>
                {careers.length > 0 && (
                  <div className="admin-list-toolbar">
                    <select
                      className="status-select"
                      value={careerFilterStatus}
                      onChange={(e) => {
                        setCareerFilterStatus(e.target.value);
                        patchRequestListPaging('careers', { page: 1 });
                      }}
                      aria-label="Filter by status"
                    >
                      <option value="all">All statuses</option>
                      <option value="new">New</option>
                      <option value="reviewing">Reviewing</option>
                      <option value="shortlisted">Shortlisted</option>
                      <option value="rejected">Rejected</option>
                    </select>
                    <AdminRequestDateFilters
                      dateFrom={careersPaging.dateFrom}
                      dateTo={careersPaging.dateTo}
                      pageSize={careersPaging.pageSize}
                      onDateFrom={(v) => patchRequestListPaging('careers', { dateFrom: v, page: 1 })}
                      onDateTo={(v) => patchRequestListPaging('careers', { dateTo: v, page: 1 })}
                      onPageSize={(n) => patchRequestListPaging('careers', { pageSize: n, page: 1 })}
                    />
                    <input
                      type="search"
                      className="admin-filter-search"
                      placeholder="Search name, email, phone, role…"
                      value={careerSearch}
                      onChange={(e) => {
                        setCareerSearch(e.target.value);
                        patchRequestListPaging('careers', { page: 1 });
                      }}
                      aria-label="Search applications"
                    />
                    <button
                      type="button"
                      className="add-btn admin-export-btn"
                      onClick={() =>
                        downloadCsv(
                          'career-applications.csv',
                          ['timestamp', 'status', 'name', 'email', 'phone', 'role', 'resumeURL'],
                          filteredCareers.map((c) => [
                            formatTs(c.timestamp),
                            c.status || 'new',
                            c.name,
                            c.email,
                            c.phone || '',
                            c.role || '',
                            c.resumeURL || '',
                          ])
                        )
                      }
                    >
                      <FaDownload /> Export CSV ({filteredCareers.length})
                    </button>
                  </div>
                )}
                {careers.length === 0 ? (
                  <div className="empty-state-admin">
                    <p>No career applications yet.</p>
                  </div>
                ) : filteredCareers.length === 0 ? (
                  <div className="empty-state-admin">
                    <p>No applications match your filters.</p>
                  </div>
                ) : (
                  <div className="applications-list">
                    {pagedCareers.map((career) => (
                      <div key={career.id} className="application-item">
                        {editingCareer?.id === career.id ? (
                          <form onSubmit={handleUpdateCareer} className="member-form-inline" style={{ margin: 0 }}>
                            <h3>Edit Application</h3>
                            <input type="text" name="name" defaultValue={career.name} placeholder="Name" required />
                            <input type="email" name="email" defaultValue={career.email} placeholder="Email" required />
                            <input type="tel" name="phone" defaultValue={career.phone} placeholder="Phone" />
                            <input type="text" name="role" defaultValue={career.role || ''} placeholder="Role applied for" />
                            <select name="status" defaultValue={career.status || 'new'}>
                              <option value="new">New</option>
                              <option value="reviewing">Reviewing</option>
                              <option value="shortlisted">Shortlisted</option>
                              <option value="rejected">Rejected</option>
                            </select>
                            <div className="form-actions">
                              <button type="submit" className="save-btn">Save</button>
                              <button type="button" onClick={() => setEditingCareer(null)} className="cancel-btn">Cancel</button>
                            </div>
                          </form>
                        ) : (
                          <>
                            <div className="application-header">
                              <h3>{career.name}</h3>
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <select
                                  value={career.status || 'new'}
                                  onChange={(e) => handleUpdateCareerStatus(career.id, e.target.value)}
                                  className="status-select"
                                >
                                  <option value="new">New</option>
                                  <option value="reviewing">Reviewing</option>
                                  <option value="shortlisted">Shortlisted</option>
                                  <option value="rejected">Rejected</option>
                                </select>
                                <button onClick={() => setEditingCareer(career)} className="edit-btn" style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}><FaEdit /> Edit</button>
                                <button
                                  type="button"
                                  onClick={() => void handleDeleteCareer(career)}
                                  className="delete-btn"
                                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                                  disabled={careerDeletingId === career.id}
                                  aria-label={`Delete career application from ${career.name}`}
                                >
                                  <FaTrash /> {careerDeletingId === career.id ? '…' : 'Delete'}
                                </button>
                              </div>
                            </div>
                            <p><strong>Email:</strong> {career.email}</p>
                            <p><strong>Phone:</strong> {career.phone}</p>
                            {career.role && <p><strong>Role:</strong> {career.role}</p>}
                            {career.resumeURL && (
                              <p><a href={career.resumeURL} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--blue)', fontSize: '0.85rem' }}>📄 Download Resume</a></p>
                            )}
                            <p className="timestamp">{career.timestamp?.toDate().toLocaleString()}</p>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {careers.length > 0 && filteredCareers.length > 0 && (
                  <AdminRequestPaginationRow
                    page={careersPage}
                    totalPages={careersTotalPages}
                    totalFiltered={filteredCareers.length}
                    onPrev={() => patchRequestListPaging('careers', { page: careersPage - 1 })}
                    onNext={() => patchRequestListPaging('careers', { page: careersPage + 1 })}
                  />
                )}
              </div>
            )}

            {activeTab === 'deck' && (
              <div className="deck-panel">
                <div className="panel-header">
                  <h2>Investor deck (live site)</h2>
                </div>
                <div className="content-item" style={{ marginBottom: '2rem' }}>
                  <h3>PDF &amp; slide images</h3>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                    Files for the public <strong>/fund-deck</strong> viewer: PDF on the default Storage bucket; WebP slides use a dedicated bucket when available, otherwise <code>fund-deck/pages/</code> on the same bucket (download-token URLs). Site <strong>Content</strong> tab is only for homepage stats and other copy — deck files are managed here.
                  </p>
                  <p style={{ fontSize: '0.85rem', color: 'var(--gray)', marginBottom: '1rem' }}>
                    Fund deck PDF in Storage (<code>fund-deck/fund-deck.pdf</code>):{' '}
                    <strong style={{ color: sampleDeckPdfExists ? '#15803d' : '#b91c1c' }}>
                      {sampleDeckPdfExists === null ? '…' : sampleDeckPdfExists ? 'Found' : 'Not found'}
                    </strong>
                  </p>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                    Upload replaces the PDF (no prior versions). Regenerate WebP pages after upload so the viewer can load images.
                  </p>
                  {(sampleDeckProcessingStatus || sampleDeckPageCount > 0) && (
                    <p style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: 'var(--gray)' }}>
                      Firestore status:{' '}
                      <strong>
                        {sampleDeckProcessingStatus === 'pending' ? 'processing' : sampleDeckProcessingStatus || '—'}
                      </strong>
                      {sampleDeckPageCount > 0 && (
                        <>
                          {' '}
                          · {sampleDeckPageCount} WebP slide{sampleDeckPageCount === 1 ? '' : 's'}
                        </>
                      )}
                      {sampleDeckProcessingError && (
                        <span style={{ display: 'block', color: '#b91c1c', marginTop: '0.35rem' }}>{sampleDeckProcessingError}</span>
                      )}
                    </p>
                  )}
                  {sampleDeckUrl && (
                    <p style={{ marginBottom: '0.75rem' }}>
                      <a href={sampleDeckUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--blue)' }}>
                        Open source PDF (admin)
                      </a>
                    </p>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
                    <label className="add-btn" style={{ cursor: 'pointer', margin: 0, display: 'inline-block' }}>
                      <FaFileUpload /> {sampleDeckUrl ? 'Replace' : 'Upload'} PDF
                      <input type="file" accept=".pdf" onChange={handleSampleDeckUpload} disabled={sampleDeckUploading || rebuildingDeckPages} style={{ display: 'none' }} />
                    </label>
                    <button
                      type="button"
                      className="cancel-btn"
                      disabled={rebuildingDeckPages || sampleDeckUploading || !sampleDeckPdfExists}
                      onClick={() => runRebuildFundDeckPages(true)}
                    >
                      {rebuildingDeckPages ? 'Processing…' : 'Regenerate WebP pages'}
                    </button>
                    <button type="button" className="cancel-btn" onClick={() => void refreshSampleDeckPanel()} disabled={sampleDeckUploading || rebuildingDeckPages}>
                      Refresh status
                    </button>
                  </div>
                  {(sampleDeckUploading || rebuildingDeckPages) && (
                    <span style={{ marginTop: '0.5rem', color: 'var(--gray)', display: 'block', fontSize: '0.85rem' }}>
                      {sampleDeckUploading ? 'Uploading PDF…' : ''}
                      {rebuildingDeckPages ? 'Rasterising PDF (can take 1–3 min). This tab updates without reloading the whole dashboard.' : ''}
                    </span>
                  )}

                  <div className="admin-deck-preview-block">
                    <div className="admin-deck-preview-head">
                      <h4>Slide preview (same WebPs as investors)</h4>
                      <button
                        type="button"
                        className="cancel-btn"
                        style={{ padding: '0.35rem 0.75rem', fontSize: '0.75rem' }}
                        disabled={deckPreviewLoading || sampleDeckProcessingStatus !== 'ready'}
                        onClick={() => setDeckPreviewRefreshKey((k) => k + 1)}
                      >
                        {deckPreviewLoading ? 'Loading…' : 'Reload preview'}
                      </button>
                    </div>
                    <p style={{ fontSize: '0.8rem', color: 'var(--gray)', marginBottom: '0.75rem' }}>
                      Slide URLs use Storage download tokens (like gallery images). If previews break after a deck rebuild, use Reload preview or Refresh status.
                    </p>
                    {deckPreviewLoading && <p style={{ fontSize: '0.85rem', color: 'var(--gray)' }}>Loading slide thumbnails…</p>}
                    {deckPreviewError && (
                      <p style={{ fontSize: '0.85rem', color: '#b91c1c', marginBottom: '0.5rem' }}>{deckPreviewError}</p>
                    )}
                    {deckPreviewUrls && deckPreviewUrls.length > 0 && (
                      <div className="admin-deck-preview-strip">
                        {deckPreviewUrls.map((url, i) => (
                          <figure key={`${i}-${url.slice(-32)}`} className="admin-deck-preview-fig">
                            <img src={url} alt={`Deck slide ${i + 1}`} loading="lazy" draggable={false} />
                            <figcaption>#{i + 1}</figcaption>
                          </figure>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="panel-header" style={{ marginTop: '0.5rem' }}>
                  <h2>Deck requests</h2>
                </div>
                {deckRequests.length > 0 && (
                  <div className="admin-list-toolbar">
                    <select
                      className="status-select"
                      value={deckFilterStatus}
                      onChange={(e) => {
                        setDeckFilterStatus(e.target.value);
                        patchRequestListPaging('deck', { page: 1 });
                      }}
                      aria-label="Filter by status"
                    >
                      <option value="all">All statuses</option>
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="deck-sent">Deck Sent</option>
                      <option value="resolved">Resolved</option>
                    </select>
                    <AdminRequestDateFilters
                      dateFrom={deckListPaging.dateFrom}
                      dateTo={deckListPaging.dateTo}
                      pageSize={deckListPaging.pageSize}
                      onDateFrom={(v) => patchRequestListPaging('deck', { dateFrom: v, page: 1 })}
                      onDateTo={(v) => patchRequestListPaging('deck', { dateTo: v, page: 1 })}
                      onPageSize={(n) => patchRequestListPaging('deck', { pageSize: n, page: 1 })}
                    />
                    <input
                      type="search"
                      className="admin-filter-search"
                      placeholder="Search name, email, phone…"
                      value={deckSearch}
                      onChange={(e) => {
                        setDeckSearch(e.target.value);
                        patchRequestListPaging('deck', { page: 1 });
                      }}
                      aria-label="Search deck requests"
                    />
                    <button
                      type="button"
                      className="add-btn admin-export-btn"
                      onClick={() =>
                        downloadCsv(
                          'deck-requests.csv',
                          [
                            'timestamp',
                            'status',
                            'name',
                            'email',
                            'phone',
                            'Referred By (Distributor Name) / Organization',
                            'deckOpenedAt',
                          ],
                          filteredDeckRequests.map((r) => [
                            formatTs(r.timestamp),
                            r.status || 'new',
                            r.name,
                            r.email || '',
                            r.phone,
                            r.organisation || '',
                            formatTs(r.deckOpenedAt),
                          ])
                        )
                      }
                    >
                      <FaDownload /> Export CSV ({filteredDeckRequests.length})
                    </button>
                  </div>
                )}
                {deckRequests.length === 0 ? (
                  <div className="empty-state-admin">
                    <p>No deck requests yet.</p>
                  </div>
                ) : filteredDeckRequests.length === 0 ? (
                  <div className="empty-state-admin">
                    <p>No requests match your filters.</p>
                  </div>
                ) : (
                  <div className="inquiries-list">
                    {pagedDeckRequests.map((req) => (
                      <div key={req.id} className="inquiry-item">
                        {editingDeck?.id === req.id ? (
                          <form
                            key={req.id}
                            onSubmit={handleUpdateDeckRequest}
                            className="member-form-inline"
                            style={{ margin: 0 }}
                          >
                            <h3>Edit Deck Request</h3>
                            <input type="text" name="name" defaultValue={req.name} placeholder="Name" required />
                            <input type="tel" name="phone" defaultValue={req.phone} placeholder="Phone" required />
                            <input type="email" name="email" defaultValue={req.email || ''} placeholder="Email" />
                            <input
                              type="text"
                              name="organisation"
                              defaultValue={req.organisation || ''}
                              placeholder="Referred By (Distributor Name) / Organization"
                              aria-label="Referred By (Distributor Name) / Organization"
                            />
                            <select name="status" defaultValue={req.status || 'new'}>
                              <option value="new">New</option>
                              <option value="contacted">Contacted</option>
                              <option value="deck-sent">Deck Sent</option>
                              <option value="resolved">Resolved</option>
                            </select>
                            <div className="form-actions">
                              <button type="submit" className="save-btn">Save</button>
                              <button type="button" onClick={() => setEditingDeck(null)} className="cancel-btn">Cancel</button>
                            </div>
                          </form>
                        ) : (
                          <>
                            <div className="inquiry-header">
                              <h3>{req.name}</h3>
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <select
                                  value={req.status || 'new'}
                                  onChange={(e) => handleUpdateDeckStatus(req.id, e.target.value)}
                                  className="status-select"
                                >
                                  <option value="new">New</option>
                                  <option value="contacted">Contacted</option>
                                  <option value="deck-sent">Deck Sent</option>
                                  <option value="resolved">Resolved</option>
                                </select>
                                <button
                                  type="button"
                                  onClick={() => setEditingDeck(req)}
                                  className="edit-btn"
                                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                                >
                                  <FaEdit /> Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void handleDeleteDeckRequest(req)}
                                  className="delete-btn"
                                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                                  disabled={deckDeletingId === req.id}
                                  aria-label={`Delete deck request for ${req.name}`}
                                >
                                  <FaTrash /> {deckDeletingId === req.id ? '…' : 'Delete'}
                                </button>
                              </div>
                            </div>
                            <p><strong>Phone:</strong> <a href={`tel:${req.phone}`}>{req.phone}</a></p>
                            {req.email && <p><strong>Email:</strong> <a href={`mailto:${req.email}`}>{req.email}</a></p>}
                            {req.organisation && (
                              <p>
                                <strong>Referred By (Distributor Name) / Organization:</strong> {req.organisation}
                              </p>
                            )}
                            <p className="timestamp">Submitted: {req.timestamp?.toDate().toLocaleString()}</p>
                            {req.deckOpenedAt?.toDate && (
                              <p className="timestamp" style={{ color: 'var(--accent-blue)' }}>
                                Deck opened: {req.deckOpenedAt.toDate().toLocaleString()}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {deckRequests.length > 0 && filteredDeckRequests.length > 0 && (
                  <AdminRequestPaginationRow
                    page={deckListPage}
                    totalPages={deckListTotalPages}
                    totalFiltered={filteredDeckRequests.length}
                    onPrev={() => patchRequestListPaging('deck', { page: deckListPage - 1 })}
                    onNext={() => patchRequestListPaging('deck', { page: deckListPage + 1 })}
                  />
                )}
              </div>
            )}

            {activeTab === 'governance' && (
              <div className="deck-panel">
                <div className="panel-header">
                  <h2>Governance / IR Document Requests</h2>
                </div>
                {governanceRequests.length > 0 && (
                  <div className="admin-list-toolbar">
                    <select
                      className="status-select"
                      value={govFilterStatus}
                      onChange={(e) => {
                        setGovFilterStatus(e.target.value);
                        patchRequestListPaging('governance', { page: 1 });
                      }}
                      aria-label="Filter by status"
                    >
                      <option value="all">All statuses</option>
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="sent">Sent</option>
                      <option value="resolved">Resolved</option>
                    </select>
                    <select
                      className="status-select"
                      value={govFilterDoc}
                      onChange={(e) => {
                        setGovFilterDoc(e.target.value);
                        patchRequestListPaging('governance', { page: 1 });
                      }}
                      aria-label="Filter by document"
                    >
                      <option value="all">All documents</option>
                      {governanceDocKeys.map((k) => (
                        <option key={k} value={k}>
                          {GOVERNANCE_DOC_LABELS[k] || k}
                        </option>
                      ))}
                    </select>
                    <AdminRequestDateFilters
                      dateFrom={govPaging.dateFrom}
                      dateTo={govPaging.dateTo}
                      pageSize={govPaging.pageSize}
                      onDateFrom={(v) => patchRequestListPaging('governance', { dateFrom: v, page: 1 })}
                      onDateTo={(v) => patchRequestListPaging('governance', { dateTo: v, page: 1 })}
                      onPageSize={(n) => patchRequestListPaging('governance', { pageSize: n, page: 1 })}
                    />
                    <input
                      type="search"
                      className="admin-filter-search"
                      placeholder="Search name, email, document…"
                      value={govSearch}
                      onChange={(e) => {
                        setGovSearch(e.target.value);
                        patchRequestListPaging('governance', { page: 1 });
                      }}
                      aria-label="Search governance requests"
                    />
                    <button
                      type="button"
                      className="add-btn admin-export-btn"
                      onClick={() =>
                        downloadCsv(
                          'governance-requests.csv',
                          ['timestamp', 'status', 'documentKey', 'documentLabel', 'name', 'email', 'phone', 'organisation'],
                          filteredGovernanceRequests.map((r) => [
                            formatTs(r.timestamp),
                            r.status || 'new',
                            r.documentKey || '',
                            r.documentLabel || '',
                            r.name,
                            r.email || '',
                            r.phone,
                            r.organisation || '',
                          ])
                        )
                      }
                    >
                      <FaDownload /> Export CSV ({filteredGovernanceRequests.length})
                    </button>
                  </div>
                )}
                {governanceRequests.length === 0 ? (
                  <div className="empty-state-admin">
                    <p>No governance document requests yet.</p>
                  </div>
                ) : filteredGovernanceRequests.length === 0 ? (
                  <div className="empty-state-admin">
                    <p>No requests match your filters.</p>
                  </div>
                ) : (
                  <div className="inquiries-list">
                    {pagedGovernanceRequests.map((req) => (
                      <div key={req.id} className="inquiry-item">
                        {editingGov?.id === req.id ? (
                          <form
                            key={req.id}
                            onSubmit={handleUpdateGovernanceRequest}
                            className="member-form-inline"
                            style={{ margin: 0 }}
                          >
                            <h3>Edit Governance Request</h3>
                            <p style={{ fontSize: '0.8rem', color: 'var(--gray)', margin: '0 0 0.5rem' }}>
                              Document: <strong>{req.documentLabel}</strong> ({req.documentKey})
                            </p>
                            <input type="text" name="name" defaultValue={req.name} placeholder="Name" required />
                            <input type="tel" name="phone" defaultValue={req.phone} placeholder="Phone" required />
                            <input type="email" name="email" defaultValue={req.email || ''} placeholder="Email" />
                            <input type="text" name="organisation" defaultValue={req.organisation || ''} placeholder="Organisation" />
                            <select name="status" defaultValue={req.status || 'new'}>
                              <option value="new">New</option>
                              <option value="contacted">Contacted</option>
                              <option value="sent">Sent</option>
                              <option value="resolved">Resolved</option>
                            </select>
                            <div className="form-actions">
                              <button type="submit" className="save-btn">Save</button>
                              <button type="button" onClick={() => setEditingGov(null)} className="cancel-btn">Cancel</button>
                            </div>
                          </form>
                        ) : (
                          <>
                            <div className="inquiry-header">
                              <h3>{req.name}</h3>
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '0.75rem', color: 'var(--gray)' }}>{req.documentLabel}</span>
                                <select
                                  value={req.status || 'new'}
                                  onChange={(e) => handleUpdateGovernanceStatus(req.id, e.target.value)}
                                  className="status-select"
                                >
                                  <option value="new">New</option>
                                  <option value="contacted">Contacted</option>
                                  <option value="sent">Sent</option>
                                  <option value="resolved">Resolved</option>
                                </select>
                                <button
                                  type="button"
                                  onClick={() => setEditingGov(req)}
                                  className="edit-btn"
                                  style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                                >
                                  <FaEdit /> Edit
                                </button>
                              </div>
                            </div>
                            <p><strong>Phone:</strong> <a href={`tel:${req.phone}`}>{req.phone}</a></p>
                            {req.email && (
                              <p>
                                <strong>Email:</strong> <a href={`mailto:${req.email}`}>{req.email}</a>
                              </p>
                            )}
                            {req.organisation && <p><strong>Organisation:</strong> {req.organisation}</p>}
                            <p className="timestamp">{req.timestamp?.toDate().toLocaleString()}</p>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {governanceRequests.length > 0 && filteredGovernanceRequests.length > 0 && (
                  <AdminRequestPaginationRow
                    page={govPage}
                    totalPages={govTotalPages}
                    totalFiltered={filteredGovernanceRequests.length}
                    onPrev={() => patchRequestListPaging('governance', { page: govPage - 1 })}
                    onNext={() => patchRequestListPaging('governance', { page: govPage + 1 })}
                  />
                )}
              </div>
            )}

            {activeTab === 'reports' && (
              <div className="reports-admin-panel">
                <div className="panel-header">
                  <h2>Reports & Documents</h2>
                  <button onClick={() => { setEditingReport(null); setShowReportForm(true); }} className="add-btn">
                    <FaPlus /> Upload Report
                  </button>
                </div>
                {(showReportForm || editingReport) && (
                  <div className="inline-form-container">
                    <form onSubmit={editingReport ? handleUpdateReport : handleAddReport} className="member-form-inline">
                      <h3>{editingReport ? 'Edit Report' : 'Upload Report'}</h3>
                      <input type="text" name="title" placeholder="Report Title" required defaultValue={editingReport?.title} />
                      <select name="type" required defaultValue={editingReport?.type}>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="annual">Annual</option>
                      </select>
                      <input
                        type="date"
                        name="date"
                        required
                        defaultValue={editingReport?.date?.toDate?.()?.toISOString?.()?.slice(0, 10)}
                      />
                      {!editingReport && <input type="file" name="file" accept=".pdf" required />}
                      {editingReport && <p style={{ fontSize: '0.8rem', color: 'var(--gray)' }}>PDF unchanged. Delete and re-upload to replace file.</p>}
                      <label className="admin-checkbox-label">
                        <input type="checkbox" name="published" defaultChecked={editingReport?.published ?? true} />
                        <span>{editingReport ? 'Published' : 'Publish immediately'}</span>
                      </label>
                      <div className="form-actions">
                        <button type="submit" className="save-btn">{editingReport ? 'Update' : 'Upload'}</button>
                        <button type="button" onClick={() => { setShowReportForm(false); setEditingReport(null); }} className="cancel-btn">Cancel</button>
                      </div>
                    </form>
                  </div>
                )}
                <div className="reports-admin-list">
                  {reports.map((r) => (
                    <div key={r.id} className="inquiry-item">
                      <div className="inquiry-header">
                        <h3>{r.title}</h3>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '0.75rem', color: 'var(--gray)' }}>{r.type}</span>
                          <button onClick={() => handleToggleReportPublish(r)} className={`status-select ${r.published ? '' : 'unpublished'}`}>
                            {r.published ? 'Published' : 'Draft'}
                          </button>
                          <button onClick={() => { setShowReportForm(false); setEditingReport(r); }} className="edit-btn" style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}><FaEdit /> Edit</button>
                          <a href={r.fileURL} target="_blank" rel="noopener noreferrer" className="download-btn" style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}>View</a>
                          <button onClick={() => handleDeleteReport(r.id)} className="delete-btn" style={{ padding: '0.4rem 0.8rem' }}><FaTrash /></button>
                        </div>
                      </div>
                      <p className="timestamp">{r.date?.toDate?.()?.toLocaleDateString?.() ?? '—'}</p>
                    </div>
                  ))}
                </div>
                {reports.length === 0 && !showReportForm && (
                  <div className="empty-state-admin">
                    <p>No reports uploaded yet.</p>
                    <button onClick={() => setShowReportForm(true)} className="add-btn"><FaPlus /> Upload First Report</button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'blog' && (
              <div className="blog-admin-panel">
                <div className="panel-header">
                  <h2>Blog / Insights</h2>
                  <button onClick={() => { setEditingBlog(null); setShowBlogForm(true); }} className="add-btn">
                    <FaPlus /> New Post
                  </button>
                </div>
                {(showBlogForm || editingBlog) && (
                  <div className="inline-form-container">
                    <form onSubmit={handleSaveBlog} className="member-form-inline">
                      <h3>{editingBlog ? 'Edit Post' : 'New Post'}</h3>
                      <input type="text" name="title" placeholder="Title" defaultValue={editingBlog?.title} required />
                      <input
                        type="text"
                        name="slug"
                        placeholder="URL path (optional — generated from title if empty)"
                        defaultValue={editingBlog?.slug || ''}
                        autoComplete="off"
                        aria-describedby="blog-slug-hint"
                      />
                      <p id="blog-slug-hint" style={{ fontSize: '0.8rem', color: 'var(--gray)', marginTop: '-0.35rem', marginBottom: '0.75rem' }}>
                        Public URL: <code style={{ fontSize: '0.78rem' }}>/insights/your-article-title</code> — generated from the title when left blank.
                        Clear this field and save to regenerate from the current title. Custom paths use letters, numbers, and hyphens only.
                        {editingBlog?.slug ? (
                          <>
                            {' '}
                            Saved path: <code>{editingBlog.slug}</code>
                          </>
                        ) : null}
                      </p>
                      <textarea
                        name="excerpt"
                        placeholder="Short summary for cards and listings (plain text)"
                        rows={3}
                        defaultValue={editingBlog?.excerpt}
                        required
                      />
                      <BlogContentEditor
                        key={editingBlog?.id ?? 'new-post'}
                        id="admin-blog-body"
                        value={blogContentHtml}
                        onChange={setBlogContentHtml}
                      />
                      <div style={{ marginBottom: '0.75rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.35rem', fontSize: '0.9rem' }}>Featured image (optional)</label>
                        <p style={{ fontSize: '0.78rem', color: 'var(--gray)', marginBottom: '0.35rem', lineHeight: 1.45 }}>
                          <strong>JPG or PNG</strong> is ideal for link previews. <strong>SVG / AVIF</strong> covers still work: the site generates a JPEG copy for social crawlers (first share may take a few seconds).
                        </p>
                        {editingBlog?.imageURL && (
                          <p style={{ fontSize: '0.8rem', color: 'var(--gray)', marginBottom: '0.35rem' }}>
                            Current: <a href={editingBlog.imageURL} target="_blank" rel="noopener noreferrer">View</a>. Upload a new file to replace.
                          </p>
                        )}
                        <input ref={blogImageInputRef} type="file" accept="image/*" style={{ fontSize: '0.85rem' }} />
                      </div>
                      <label className="admin-checkbox-label">
                        <input type="checkbox" name="published" defaultChecked={editingBlog?.published} />
                        <span>Publish</span>
                      </label>
                      <div className="form-actions">
                        <button type="submit" className="save-btn" disabled={blogImageUploading}>{blogImageUploading ? 'Uploading image...' : 'Save'}</button>
                        <button type="button" onClick={() => { setShowBlogForm(false); setEditingBlog(null); }} className="cancel-btn">Cancel</button>
                      </div>
                    </form>
                  </div>
                )}
                <div className="applications-list">
                  {blogPosts.map((post) => (
                    <div key={post.id} className="application-item">
                      <div className="application-header">
                        <h3>{post.title}</h3>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <button onClick={() => handleToggleBlogPublish(post)} className={`status-select ${post.published ? '' : 'unpublished'}`}>
                            {post.published ? 'Published' : 'Draft'}
                          </button>
                          <button onClick={() => { setEditingBlog(post); setShowBlogForm(true); }} className="edit-btn"><FaEdit /> Edit</button>
                          <button onClick={() => handleDeleteBlog(post.id)} className="delete-btn"><FaTrash /></button>
                        </div>
                      </div>
                      <p className="blog-excerpt" style={{ fontSize: '0.85rem', color: 'var(--gray)', marginTop: '0.5rem' }}>{post.excerpt?.slice(0, 120)}...</p>
                      <p className="timestamp">{post.date?.toDate?.()?.toLocaleDateString?.() ?? '—'}</p>
                    </div>
                  ))}
                </div>
                {blogPosts.length === 0 && !showBlogForm && !editingBlog && (
                  <div className="empty-state-admin">
                    <p>No blog posts yet.</p>
                    <button onClick={() => setShowBlogForm(true)} className="add-btn"><FaPlus /> Create First Post</button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'investors' && (
              <div className="investors-panel">
                <div className="panel-header">
                  <h2>Investor Whitelist</h2>
                </div>
                <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  Only whitelisted investors can request a magic link to access the investor portal. Add a single email or upload a CSV with one email per line (or comma/semicolon separated).
                </p>
                <p style={{ marginBottom: '1rem', fontSize: '0.8rem', color: 'var(--gray)' }}>
                  <strong>Add from CSV</strong> — Merges new emails into the existing whitelist. &bull; <strong>Replace All from CSV</strong> — Removes all current emails and replaces with your file. Hover over buttons for details.
                </p>
                <form onSubmit={handleAddSingleEmail} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                  <input
                    type="email"
                    placeholder="investor@example.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem', border: '1px solid var(--gray-lt)', borderRadius: 4, minWidth: 200 }}
                  />
                  <button type="submit" className="add-btn" disabled={addingEmail}>
                    {addingEmail ? 'Adding...' : 'Add Email'}
                  </button>
                </form>
                <div className="investors-upload-actions" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem', alignItems: 'center' }}>
                  <label className="add-btn" style={{ cursor: 'pointer', margin: 0 }} title="Add new emails from a CSV file. Existing whitelist is kept; new emails are merged in.">
                    <FaFileUpload /> Add from CSV
                    <input type="file" accept=".csv,.txt" onChange={handleCsvUpload} disabled={csvUploading} style={{ display: 'none' }} />
                  </label>
                  <label className="cancel-btn" style={{ cursor: 'pointer', margin: 0 }} title="Replace the entire whitelist with emails from your CSV. All current emails will be removed. Use with caution.">
                    Replace All from CSV
                    <input type="file" accept=".csv,.txt" onChange={handleReplaceWhitelist} disabled={csvUploading} style={{ display: 'none' }} />
                  </label>
                  <button type="button" onClick={handleDownloadInvestorCsvSample} className="cancel-btn" style={{ margin: 0 }} title="Download a sample CSV file showing the correct format (one email per line).">
                    Download Sample CSV
                  </button>
                  {csvUploading && <span style={{ color: 'var(--gray)' }}>Uploading...</span>}
                </div>
                <div className="investors-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {investorEmails.map((email) => (
                    <div key={email} className="inquiry-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {editingInvestorEmail === email ? (
                        <form onSubmit={handleSaveInvestorEmail} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flex: 1, minWidth: 200 }}>
                          <input
                            type="email"
                            value={editingInvestorValue}
                            onChange={(e) => setEditingInvestorValue(e.target.value)}
                            style={{ flex: 1, padding: '0.4rem 0.6rem', fontSize: '0.85rem', border: '1px solid var(--gray-lt)', borderRadius: 4 }}
                          />
                          <button type="submit" className="save-btn" style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}>Save</button>
                          <button type="button" onClick={() => { setEditingInvestorEmail(null); setEditingInvestorValue(''); }} className="cancel-btn" style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}>Cancel</button>
                        </form>
                      ) : (
                        <>
                          <span>{email}</span>
                          <div style={{ display: 'flex', gap: '0.35rem' }}>
                            <button onClick={() => handleEditInvestorEmail(email)} className="edit-btn" style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}><FaEdit /> Edit</button>
                            <button onClick={() => handleRemoveInvestorEmail(email)} className="delete-btn" style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem' }}><FaTrash /> Remove</button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                {investorEmails.length === 0 && (
                  <div className="empty-state-admin">
                    <p>No investors in whitelist. Upload a CSV to add allowed investor emails.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'content' && (
              <div className="content-panel">
                <div className="panel-header">
                  <h2>Site content</h2>
                </div>
                <div className="content-item">
                  <h3>Homepage stats</h3>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                    Key numbers displayed on the homepage hero section.
                  </p>
                  <form onSubmit={handleSaveHomeStats} className="member-form-inline" style={{ marginBottom: '2rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div>
                        <label style={{ fontSize: '0.8rem', color: 'var(--gray)', display: 'block', marginBottom: '0.25rem' }}>Target IRR</label>
                        <input type="text" name="targetIRR" defaultValue={homeStats.targetIRR} style={{ width: '100%', padding: '0.5rem' }} />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.8rem', color: 'var(--gray)', display: 'block', marginBottom: '0.25rem' }}>Min. Investment</label>
                        <input type="text" name="minInvestment" defaultValue={homeStats.minInvestment} style={{ width: '100%', padding: '0.5rem' }} />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.8rem', color: 'var(--gray)', display: 'block', marginBottom: '0.25rem' }}>Transactions Advised</label>
                        <input type="text" name="transactionsAdvised" defaultValue={homeStats.transactionsAdvised} style={{ width: '100%', padding: '0.5rem' }} />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.8rem', color: 'var(--gray)', display: 'block', marginBottom: '0.25rem' }}>Years Asian Markets</label>
                        <input type="text" name="yearsAsianMarkets" defaultValue={homeStats.yearsAsianMarkets} style={{ width: '100%', padding: '0.5rem' }} />
                      </div>
                    </div>
                    <button type="submit" className="save-btn" style={{ marginTop: '1rem', flex: 'none' }}>Save Stats</button>
                  </form>
                </div>
                <p style={{ fontSize: '0.85rem', color: 'var(--gray)', marginTop: '0.5rem' }}>
                  Fund deck PDF and WebP slides are managed under the <strong>Deck</strong> tab (top: investor deck; below: requests).
                </p>
              </div>
            )}

            {activeTab === 'deals' && (
              <div className="content-panel">
                <div className="panel-header">
                  <h2>Advisory Deals & Galleries</h2>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button onClick={() => { setEditingDeal(null); setShowDealForm(true); }} className="add-btn"><FaPlus /> Add Deal</button>
                    {deals.length === 0 && (
                      <button onClick={handleSeedDeals} className="cancel-btn">Seed Default Deals</button>
                    )}
                  </div>
                </div>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                  Deal cards and gallery images shown on the Advisory page. Use the ↑ / ↓ arrows to change the order deal cards appear in. Add deals and upload gallery images for each listing (EPW, Bhadora, etc).
                </p>

                <div className="content-item" style={{ marginBottom: '2rem' }}>
                  <h3>Deal Cards</h3>
                  {(showDealForm || editingDeal) && (
                    <form onSubmit={handleSaveDeal} className="member-form-inline" style={{ marginBottom: '1.5rem' }}>
                      <h4>{editingDeal ? 'Edit Deal' : 'Add Deal'}</h4>
                      <input type="text" name="type" placeholder="Type (e.g. PRE-IPO FUNDRAISE, IPO)" required defaultValue={editingDeal?.type} />
                      <select name="typeClass" defaultValue={editingDeal?.typeClass || 'ipo'}>
                        <option value="preipo">Pre-IPO</option>
                        <option value="ipo">IPO</option>
                        <option value="debt">Debt</option>
                      </select>
                      <input type="text" name="company" placeholder="Company Name" required defaultValue={editingDeal?.company} />
                      <input type="text" name="sector" placeholder="Sector" required defaultValue={editingDeal?.sector} />
                      <input type="text" name="amount" placeholder="Amount (e.g. ₹11.5 Cr)" required defaultValue={editingDeal?.amount} />
                      <input type="text" name="amountLabel" placeholder="Amount Label (e.g. Pre-IPO Round Raised)" required defaultValue={editingDeal?.amountLabel} />
                      <textarea name="desc" placeholder="Description" rows={3} required defaultValue={editingDeal?.desc} />
                      <input type="text" name="date" placeholder="Date (e.g. Aug 2024)" required defaultValue={editingDeal?.date} />
                      <div className="form-actions">
                        <button type="submit" className="save-btn">Save</button>
                        <button type="button" onClick={() => { setEditingDeal(null); setShowDealForm(false); }} className="cancel-btn">Cancel</button>
                      </div>
                    </form>
                  )}
                  <div className="applications-list">
                    {deals.map((d, i) => (
                      <div key={d.id} className="inquiry-item">
                        <div className="inquiry-header">
                          <h3>{i + 1}. {d.company} · {d.type}</h3>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button type="button" onClick={() => handleMoveDeal(i, -1)} className="edit-btn" disabled={i === 0 || dealReordering} title="Move up" aria-label={`Move ${d.company} up`}><FaArrowUp /></button>
                            <button type="button" onClick={() => handleMoveDeal(i, 1)} className="edit-btn" disabled={i === deals.length - 1 || dealReordering} title="Move down" aria-label={`Move ${d.company} down`}><FaArrowDown /></button>
                            <button onClick={() => { setEditingDeal(d); setShowDealForm(false); }} className="edit-btn"><FaEdit /> Edit</button>
                            <button onClick={() => handleDeleteDeal(d.id)} className="delete-btn"><FaTrash /></button>
                          </div>
                        </div>
                        <p style={{ fontSize: '0.85rem', color: 'var(--gray)' }}>{d.sector} · {d.amount} · {d.date}</p>
                      </div>
                    ))}
                  </div>
                  {deals.length === 0 && !showDealForm && (
                    <p style={{ color: 'var(--gray)', marginTop: '1rem' }}>No deals yet. Add one or seed defaults.</p>
                  )}
                </div>

                <div className="content-item">
                  <h3>Deal Galleries</h3>
                  <p style={{ fontSize: '0.85rem', color: 'var(--gray)', marginBottom: '1rem' }}>
                    Shown on the public Advisory page under “Some of the Glimpses”. Use a unique <strong>slug</strong> per listing (e.g. <code>epw</code>, <code>bhadora</code>) — it becomes the Storage folder for uploads. Use the <strong>↑ / ↓</strong> arrows to change the order galleries appear in.
                  </p>
                  <p style={{ fontSize: '0.8rem', color: 'var(--gray)', marginBottom: '1rem', padding: '0.65rem 0.75rem', background: 'var(--off)', borderRadius: 6, border: '1px solid var(--gray-lt)' }}>
                    <strong>New gallery:</strong> fill slug, title, caption, and click Save. Then use <strong>Add Image</strong> on that gallery’s row (images cannot upload until the gallery exists in Firestore).
                  </p>
                  {!editingGallery && dealGalleries.length > 0 && (
                    <button onClick={() => setEditingGallery({ id: 'new', slug: '', title: '', caption: '', imageURLs: [], order: dealGalleries.length })} className="add-btn" style={{ marginBottom: '1rem' }}><FaPlus /> Add Gallery</button>
                  )}
                  {dealGalleries.map((g, i) => (
                    <div key={g.id} className="inquiry-item" style={{ marginBottom: '1.5rem' }}>
                      <div className="inquiry-header">
                        <h3>{i + 1}. {g.title || g.slug}</h3>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <button type="button" onClick={() => handleMoveGallery(i, -1)} className="edit-btn" disabled={i === 0 || dealReordering} title="Move up" aria-label={`Move ${g.title || g.slug} up`}><FaArrowUp /></button>
                          <button type="button" onClick={() => handleMoveGallery(i, 1)} className="edit-btn" disabled={i === dealGalleries.length - 1 || dealReordering} title="Move down" aria-label={`Move ${g.title || g.slug} down`}><FaArrowDown /></button>
                          <button type="button" onClick={() => setEditingGallery(g)} className="edit-btn"><FaEdit /> Edit</button>
                          <button type="button" onClick={() => handleDeleteGallery(g.id, g.title || g.slug)} className="delete-btn"><FaTrash /> Delete</button>
                        </div>
                      </div>
                      <p style={{ fontSize: '0.75rem', color: 'var(--gray)' }}>Slug: <code>{g.slug || '—'}</code> · Order: {g.order}</p>
                      {g.caption && <p style={{ fontSize: '0.85rem', color: 'var(--gray)', marginBottom: '0.5rem' }}>{g.caption}</p>}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem' }}>
                        {(g.imageURLs || []).map((url, i) => (
                          <div key={i} style={{ position: 'relative' }}>
                            <img src={url} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }} />
                            <button type="button" onClick={() => handleRemoveGalleryImage(g.id, i)} className="delete-btn" style={{ position: 'absolute', top: 2, right: 2, padding: '0.2rem 0.4rem', fontSize: '0.7rem' }}>×</button>
                          </div>
                        ))}
                        <label className="add-btn" style={{ cursor: 'pointer', margin: 0, padding: '0.5rem', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                          <FaFileUpload /> {galleryImageUploading === g.id ? 'Uploading...' : 'Add Image'}
                          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleGalleryImageUpload(g.id, f); e.target.value = ''; }} disabled={galleryImageUploading === g.id} />
                        </label>
                      </div>
                    </div>
                  ))}
                  {(editingGallery || dealGalleries.length === 0) && (
                    <form
                      key={editingGallery?.id ?? 'create'}
                      onSubmit={handleSaveGallery}
                      className="member-form-inline"
                      style={{ marginTop: '1rem' }}
                    >
                      <h4>{editingGallery?.id && editingGallery.id !== 'new' ? 'Edit Gallery' : 'Add Gallery'}</h4>
                      <input type="text" name="slug" placeholder="Slug (e.g. epw, bhadora)" required defaultValue={editingGallery?.slug} />
                      <input type="text" name="title" placeholder="Title (e.g. EPW India Limited)" required defaultValue={editingGallery?.title} />
                      <textarea name="caption" placeholder="Caption (shown on Advisory page above the image grid)" rows={3} defaultValue={editingGallery?.caption} />
                      <label style={{ fontSize: '0.85rem', color: 'var(--gray)', display: 'block', marginBottom: '0.35rem' }}>
                        Display order (0 = first)
                        <input
                          type="number"
                          name="order"
                          min={0}
                          step={1}
                          defaultValue={
                            editingGallery?.id && editingGallery.id !== 'new'
                              ? editingGallery.order
                              : dealGalleries.length
                          }
                          style={{ display: 'block', width: '100%', maxWidth: 120, marginTop: '0.25rem', padding: '0.5rem' }}
                        />
                      </label>
                      <div className="form-actions">
                        <button type="submit" className="save-btn">Save</button>
                        <button type="button" onClick={() => setEditingGallery(null)} className="cancel-btn">Cancel</button>
                      </div>
                    </form>
                  )}
                  {dealGalleries.length === 0 && !editingGallery && (
                    <p style={{ color: 'var(--gray)', marginTop: '1rem' }}>No galleries yet. Add one to start uploading images.</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
